<template>
  <div class="assessment-details-container">
    <!-- Selected Assessment Header -->
    <div class="px-4 py-3 bg-blue-50 border-b border-blue-200">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-blue-900 flex items-center gap-2">
            <i class="pi pi-file-edit"></i>
            Current Schema: {{
             currentAssessmentSchema?.representationProperties?.assessmentName?.en
              || 'Unnamed Schema'
            }}
            <span
              v-if="currentSchemaNeedsMigration"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-medium
               bg-amber-100 text-amber-800"
              :title="'Unmigrated: ' + unmigratedBlocksList
               + '. Run migration from Developer &gt; Migrations.'"
            >
              <i class="pi pi-exclamation-triangle"></i>
              Unmigrated config
            </span>
          </h2>
          <p class="text-sm text-blue-600 mt-1">
            Type: {{ currentAssessmentSchema?.type }} | ID: {{ currentAssessmentSchema?._id }}
          </p>
        </div>
        <div class="text-right">
          <span class="inline-flex items-center px-2 py-1
           rounded-full text-xs font-medium bg-green-100 text-green-800">
            <i class="pi pi-check-circle mr-1"></i>
            Active
          </span>
        </div>
      </div>
    </div>

    <Tabs value="0">
      <TabList>
        <Tab value="0">Basic Info</Tab>
        <Tab value="1">Configuration</Tab>
        <Tab value="2">Intro Text</Tab>
        <Tab value="3">Questions</Tab>
        <Tab value="4">Dimensions</Tab>
        <Tab value="5">Charts</Tab>
        <Tab value="6">Developer Settings</Tab>
        <!-- Schema Templates tab hidden - templates are now managed at tenant level -->
        <!-- <Tab value="6">
          <i class="pi pi-bell mr-2"></i>
          Notification Templates
        </Tab> -->
      </TabList>
      <TabPanels>
          <TabPanel value="0">
            <!-- Basic Info content -->
            <div class="tab-content-wrapper">
              <div class="p-6" v-if="currentAssessmentSchema">
              <div class="mb-6">
                <h3 class="text-2xl font-semibold text-gray-800 mb-2">Basic Information</h3>
                <p class="text-gray-600">
                  Manage the core settings and properties of your assessment schema.
          </p>
        </div>

              <!-- Schema Type & Status -->
              <div class="bg-gradient-to-r from-blue-50 to-indigo-50
               border border-blue-200 rounded-lg p-6 mb-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h4 class="text-lg font-medium text-gray-800 mb-2">Schema Type</h4>
                    <div class="flex items-center gap-3">
                      <span class="inline-flex items-center px-3 py-1
                       rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <i class="pi pi-check-circle mr-2"></i>
                        {{ currentAssessmentSchema.type }}
              </span>
                      <span class="text-sm text-gray-500">Active Schema</span>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm text-gray-500">Schema ID</p>
                    <p class="text-sm font-mono text-gray-700">{{ currentAssessmentSchema._id }}</p>
                  </div>
                </div>
              </div>

              <!-- External Evaluation Schema Indicator -->
              <div v-if="currentAssessmentSchema.isExternalEvaluationSchema"
                   class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p class="text-amber-700">
                  <i class="pi pi-info-circle mr-2"></i>
                  This is an external evaluation schema.
                </p>
              </div>

              <!-- Grid Layout for Main Content -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

                <!-- Schema Names -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-tag mr-2 text-blue-600"></i>
                      Schema Names
                    </h4>
                  </div>
                  <div class="p-6 space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        English Name
                      </label>
                      <InputText
                        v-model="currentSchemaName.en"
                        class="w-full"
                        placeholder="Enter English name"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        German Name
                      </label>
                      <InputText
                        v-model="currentSchemaName.de"
                        class="w-full"
                        placeholder="Enter German name"
                      />
                    </div>
            <Button
                      label="Save Names"
              icon="pi pi-save"
              @click="saveAssessmentSchemaName"
                      class="w-full"
                      :disabled="!currentSchemaName.en && !currentSchemaName.de"
            />
                  </div>
          </div>

                <!-- Time Settings -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-clock mr-2 text-orange-600"></i>
                      Time Settings
                    </h4>
            </div>
                  <div class="p-6">
                    <div class="space-y-3 mb-4">
                      <div class="flex justify-between items-center py-2 border-b border-gray-100">
                        <span class="text-sm font-medium text-gray-600">Expiration Time</span>
                        <span class="text-sm text-gray-800">{{ expirationTimeInDays }} days</span>
                      </div>
                      <div class="flex justify-between items-center py-2 border-b border-gray-100">
                        <span class="text-sm font-medium text-gray-600">Reminder Time</span>
                        <span class="text-sm text-gray-800">{{ reminderTimeInDays }} days</span>
                      </div>
                      <div class="flex justify-between items-center py-2">
                        <span class="text-sm font-medium text-gray-600">Redirect Link</span>
                        <span class="text-sm text-blue-600 truncate max-w-32">
                          {{ linkAfterAssessmentDisplayLabel }}
                        </span>
                      </div>
                    </div>
              <Button
                label="Edit Time Settings"
                icon="pi pi-pencil"
                @click="openTimeSettingsOverlay"
                      class="w-full"
                      severity="secondary"
              />
            </div>
          </div>

                <!-- Role Management -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-users mr-2 text-purple-600"></i>
                      Access Control
                    </h4>
                  </div>
                  <div class="p-6">
                    <label class="block text-sm font-medium text-gray-700 mb-3">
                      Available Roles
                    </label>
              <MultiSelect
                v-model="currentAssessmentSchema.accessibleToRoles"
                :options="roleOptionsForAccessible"
                optionLabel="label"
                optionValue="value"
                optionDisabled="disabled"
                class="w-full"
                      placeholder="Select roles..."
                @change="onRolesChange()"
              />
                    <p class="text-xs text-gray-500 mt-2">
                      Users must have all selected roles (effective roles). If any role is inactive in Enums Manager,
                      this schema is unavailable for everyone.
                    </p>
            </div>
          </div>

              </div>
            </div>
            </div>
          </TabPanel>
          <TabPanel value="1">
            <!-- Configuration content -->
            <div class="tab-content-wrapper">
              <div class="p-6" v-if="currentAssessmentSchema">
              <div class="mb-6">
                <h3 class="text-2xl font-semibold text-gray-800 mb-2">Configuration Settings</h3>
                <p class="text-gray-600">
                  Configure advanced features and settings for your assessment schema.
                </p>
            </div>

              <!-- Configuration Grid -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

                <!-- Reflection Guidelines Configuration -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-refresh mr-2 text-blue-600"></i>
                      Reflection Guidelines
                    </h4>
                  </div>
                  <div class="p-6 overflow-hidden min-w-0">
                    <div class="space-y-4 min-w-0">
                      <div class="flex items-center justify-between py-3 border-b border-gray-100">
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-medium text-gray-800">
                            Enable Reflection Guidelines
                          </p>
                          <p class="text-xs text-gray-500 break-words">
                            Allow users to receive reflection guideline assessments
                          </p>
                        </div>
                  <Checkbox
                          v-model="reflexionGuidelineEnabledComputed"
                    :binary="true"
                    class="flex-shrink-0 ml-4"
                    data-testid="cms-rg-enabled"
                  />
              </div>

              <!-- Reflection Guideline Configuration Form -->
              <div v-if="reflexionGuidelineEnabledComputed" class="mt-4 space-y-4 min-w-0">
                <div v-if="!currentAssessmentSchema?._id"
                  class="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p class="text-sm text-red-700">
                    <i class="pi pi-exclamation-triangle mr-1"></i>
                    No assessment schema selected. Please select a schema first.
                  </p>
                </div>
                        <ReflexionGuidelineConfigForm
                          v-else
                          :schemaId="currentAssessmentSchema._id"
                          :initial-config="reflexionGuidelineInitialConfig"
                          @config-updated="handleReflexionGuidelineConfigUpdate"
                        />

                <div class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p class="text-sm text-blue-700">
                    <i class="pi pi-info-circle mr-1"></i>
                    Reflection guidelines will be automatically sent
                      to users based on the configured schedule.
                </p>
              </div>

                        <div class="flex gap-2">
                <Button
                            label="Test Reflection Guidelines"
                            icon="pi pi-refresh"
                            @click="testReflectionGuidelines"
                            class="flex-1"
                            severity="secondary"
                            size="small"
                          />
                          <Button
                            label="Trigger Now"
                            icon="pi pi-paper-plane"
                            @click="triggerReflectionGuidelinesNow"
                            class="flex-1"
                            severity="success"
                            size="small"
                            :loading="triggeringReflectionGuidelines"
                          />
                        </div>
              </div>

                      <div v-else class="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p class="text-sm text-gray-600">
                <i class="pi pi-info-circle mr-1"></i>
                          Enable reflection guidelines to configure assessment settings.
                        </p>
              </div>
              <div class="flex items-center justify-between py-3 border-b border-gray-100">
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-medium text-gray-800">
                            Enable View Results
                          </p>
                          <p class="text-xs text-gray-500 break-words">
                            Allow users to view the results of the submitted reflection guidelines. Reflexion guideline configuration need not be enabled for this.
                          </p>
                        </div>
                  <Checkbox
                          v-model="viewRefGuidelineResultsComputed"
                    :binary="true"
                    class="flex-shrink-0 ml-4"
                  />
              </div>
                    </div>
              </div>
            </div>

                <!-- Development Tips Configuration -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-lightbulb mr-2 text-purple-600"></i>
                      Development Tips
              </h4>
                  </div>
                  <div class="p-6 overflow-hidden" data-testid="schema-devtips-section">
                    <div class="space-y-4 min-w-0">
                      <div class="flex items-center justify-between py-3 border-b border-gray-100">
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-medium text-gray-800">Enable DevTips</p>
                          <p class="text-xs text-gray-500 break-words">
                            Send development tips to users after assessment completion
                          </p>
                        </div>
                  <Checkbox
                          v-model="devTipsEnabledComputed"
                    :binary="true"
                    class="flex-shrink-0 ml-4"
                    data-testid="cms-devtips-enabled"
                  />
              </div>

                      <div v-if="devTipsEnabledComputed" class="mt-4 space-y-3 min-w-0">
                        <!-- Delivery Frequency -->
                        <div class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                         items-start md:items-center py-3 border-b border-gray-100">
                          <div class="min-w-0">
                            <p class="text-sm font-medium text-gray-800">Delivery Frequency</p>
                            <p class="text-xs text-gray-500 break-words">
                              How often to send DevTips to users
                            </p>
                          </div>
                          <div class="w-full md:w-auto md:min-w-[8rem]">
                            <Dropdown
                              v-model="devTipsPeriodComputed"
                              :options="devTipsPeriodOptions"
                              option-label="label"
                              option-value="value"
                              class="w-full"
                              placeholder="Select frequency"
                              data-testid="cms-devtips-period"
                              :pt="{ root: { class: 'w-full' } }"
                            />
                          </div>
                        </div>

                        <!-- Schedule Settings: wall-clock (each recipient's local time) -->
                        <div class="space-y-4 pt-3 border-t border-gray-100">
                          <p class="text-sm font-medium text-gray-800">Schedule Settings</p>
                          <p class="text-xs text-gray-500">
                            Set the clock face when DevTips should arrive
                            (e.g. Mondays at 17:00). Each user receives them at that
                            time in <strong>their</strong> timezone.
                          </p>

                          <!-- Hour (recipient wall-clock) -->
                          <div class="grid grid-cols-1 md:grid-cols-[1fr_auto]
                           gap-3 items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-700">
                                Hour (0–23)
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Local hour for each recipient
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[8rem]">
                              <InputNumber
                                v-model="devTipsLocalHourComputed"
                                :min="0"
                                :max="23"
                                showButtons
                                buttonLayout="horizontal"
                                class="w-full"
                                data-testid="cms-devtips-hour"
                                :pt="{ root: { class: 'w-full' } }"
                              />
                            </div>
                          </div>

                          <!-- Day of Week (only for weekly) -->
                          <div v-if="devTipsPeriodComputed === 'weekly'"
                           class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                            items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-700">
                                Day of Week
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Which day of the week to send
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[10rem]">
                              <Dropdown
                                v-model="devTipsLocalDayOfWeekComputed"
                                :options="dayOfWeekOptions"
                                option-label="label"
                                option-value="value"
                                class="w-full"
                                data-testid="cms-devtips-dayOfWeek"
                                :pt="{ root: { class: 'w-full' } }"
                              />
                            </div>
                          </div>

                          <!-- Month (only for yearly) -->
                          <div v-if="devTipsPeriodComputed === 'yearly'"
                           class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                            items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-700">
                                Month (your local time)
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Which month to send DevTips each year
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[10rem]">
                              <Dropdown
                                v-model="devTipsLocalMonthComputed"
                                :options="monthOptions"
                                option-label="label"
                                option-value="value"
                                class="w-full"
                                data-testid="schema-devtips-month"
                                :pt="{ root: { class: 'w-full' } }"
                              />
                            </div>
                          </div>

                          <!-- Day of Month (only for monthly/yearly) -->
                          <div v-if="devTipsPeriodComputed === 'monthly'
                           || devTipsPeriodComputed === 'yearly'"
                            class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                             items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-700">
                                Day of Month
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Which day of the month to send (1-31)
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[8rem]">
                              <InputNumber
                                v-model="devTipsLocalDayOfMonthComputed"
                                :min="1"
                                :max="31"
                                showButtons
                                buttonLayout="horizontal"
                                class="w-full"
                                :pt="{ root: { class: 'w-full' } }"
                              />
                            </div>
                          </div>

                          <div
                            data-testid="cms-devtips-schedule-summary"
                            :class="[
                            'p-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700'
                          ]">
                            <span class="font-medium">Schedule:</span>
                            {{ devTipsStoredUtcLabel }}
                            <span class="text-gray-500 block mt-1">
                              Runs at this wall-clock time in each user’s timezone.
                            </span>
                          </div>
                        </div>

                        <div class="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <p class="text-sm text-purple-700">
                            <i class="pi pi-info-circle mr-1"></i>
                            DevTips will be automatically sent
                             to users after they complete this assessment.
                </p>
              </div>

                        <div class="flex gap-2">
                          <Button
                            label="Test DevTips"
                            icon="pi pi-send"
                            @click="testDevTips"
                            class="flex-1"
                            severity="secondary"
                            size="small"
                          />
                          <Button
                            label="Send Now"
                            icon="pi pi-paper-plane"
                            @click="sendDevTipsNow"
                            class="flex-1"
                            severity="success"
                            size="small"
                          />
              </div>
              </div>
                    </div>
              </div>
            </div>

                <!-- Question Rotation Configuration -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-sync mr-2 text-green-600"></i>
                      Question Rotation
              </h4>
                  </div>
                  <div class="p-6 overflow-hidden">
                    <div class="space-y-4 min-w-0">
                      <div class="flex items-center justify-between py-3 border-b border-gray-100">
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-medium text-gray-800">Enable Question Rotation</p>
                          <p class="text-xs text-gray-500 break-words">
                            Randomly select questions from available pool for each assessment
                          </p>
                        </div>
                  <Checkbox
                    v-model="questionRotationEnabledComputed"
                    :binary="true"
                    class="flex-shrink-0 ml-4"
                    data-testid="cms-qr-enabled"
                  />
              </div>

                      <div v-if="questionRotationEnabledComputed"
                       class="mt-4 space-y-3 min-w-0">
                        <!-- Rotation Frequency -->
                        <div class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                         items-start md:items-center py-3 border-b border-gray-100">
                          <div class="min-w-0">
                            <p class="text-sm font-medium text-gray-800">Rotation Frequency</p>
                            <p class="text-xs text-gray-500 break-words">
                              How often to rotate questions
                            </p>
                          </div>
                          <div class="w-full md:w-auto md:min-w-[8rem]">
                            <Dropdown
                              v-model="questionRotationPeriodComputed"
                              :options="rotationPeriodOptions"
                              option-label="label"
                              option-value="value"
                              class="w-full"
                              placeholder="Select frequency"
                              data-testid="cms-qr-period"
                              :pt="{ root: { class: 'w-full' } }"
                            />
                          </div>
                        </div>

                        <!-- Schedule Settings: wall-clock (each recipient's local time) -->
                        <div class="space-y-4 pt-3 border-t border-gray-100">
                          <p class="text-sm font-medium text-gray-800">Schedule Settings</p>
                          <p class="text-xs text-gray-500">
                            Set the clock face when rotation should run
                            (e.g. Mondays at 08:00). Applies at that time in
                            <strong>each user’s</strong> timezone.
                          </p>

                          <!-- Hour (recipient wall-clock) -->
                          <div class="grid grid-cols-1 md:grid-cols-[1fr_auto]
                           gap-3 items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-700">
                                Hour (0–23)
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Local hour for each recipient
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[8rem]">
                              <InputNumber
                                v-model="questionRotationLocalHourComputed"
                                :min="0"
                                :max="23"
                                showButtons
                                buttonLayout="horizontal"
                                class="w-full"
                                data-testid="cms-qr-hour"
                                :pt="{ root: { class: 'w-full' } }"
                              />
                            </div>
                          </div>

                          <!-- Day of Week (only for weekly) -->
                          <div v-if="questionRotationPeriodComputed === 'weekly'"
                           class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                            items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-700">
                                Day of Week
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Which day of the week to rotate
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[10rem]">
                              <Dropdown
                                v-model="questionRotationLocalDayOfWeekComputed"
                                :options="dayOfWeekOptions"
                                option-label="label"
                                option-value="value"
                                class="w-full"
                                data-testid="cms-qr-dayOfWeek"
                                :pt="{ root: { class: 'w-full' } }"
                              />
                            </div>
                          </div>

                          <!-- Day of Month (only for monthly/yearly) -->
                          <div v-if="questionRotationPeriodComputed === 'monthly'
                           || questionRotationPeriodComputed === 'yearly'"
                            class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                             items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-700">
                                Day of Month
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Which day of the month to rotate (1-31)
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[8rem]">
                              <InputNumber
                                v-model="questionRotationLocalDayOfMonthComputed"
                                :min="1"
                                :max="31"
                                showButtons
                                buttonLayout="horizontal"
                                class="w-full"
                                :pt="{ root: { class: 'w-full' } }"
                              />
                            </div>
                          </div>

                          <div
                            data-testid="cms-qr-schedule-summary"
                            :class="[
                            'p-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700'
                          ]">
                            <span class="font-medium">Schedule:</span>
                            {{ questionRotationStoredUtcLabel }}
                            <span class="text-gray-500 block mt-1">
                              Runs at this wall-clock time in each user’s timezone.
                            </span>
                          </div>
                        </div>

              <!-- Question Source -->
                        <div class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                         items-start md:items-center py-3 border-b border-gray-100">
                          <div class="min-w-0">
                            <p class="text-sm font-medium text-gray-800">Question Source</p>
                            <p class="text-xs text-gray-500 break-words">
                              Where to select questions from
                            </p>
                          </div>
                          <div class="w-full md:w-auto md:min-w-[14rem]">
                            <Dropdown
                              v-model="questionSourceComputed"
                              :options="questionSourceOptions"
                              option-label="label"
                              option-value="value"
                              class="w-full"
                              placeholder="Select source"
                              :pt="{ root: { class: 'w-full' } }"
                            />
                          </div>
                        </div>

                        <div class="py-3 border-b border-gray-100">
                          <div class="grid grid-cols-1 md:grid-cols-[1fr_auto]
                           gap-3 items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-800">
                                Questions Per Assessment
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Number of questions to select randomly
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[8rem]">
                              <InputNumber
                                v-model="localTotalQuestions"
                                :min="1"
                                :max="50"
                                showButtons
                                buttonLayout="horizontal"
                                class="w-full"
                                :pt="{ root: { class: 'w-full' } }"
                                fluid
                              />
              </div>
              </div>
              </div>

                        <!-- Sticky Questions -->
                        <div class="py-3 border-b border-gray-100">
                          <div class="grid grid-cols-1 md:grid-cols-[1fr_auto]
                           gap-3 items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-800">
                                Sticky Questions
                              </p>
                              <p class="text-xs text-gray-500 break-words">
                                Questions that appear in every rotation
                              </p>
                            </div>
                            <Checkbox
                              v-model="stickyQuestionsEnabledComputed"
                              :binary="true"
                              class="flex-shrink-0"
                            />
                          </div>

                          <div v-if="stickyQuestionsEnabledComputed" class="mt-3 space-y-3">
                            <!-- Sticky Question Source -->
                            <div class="grid grid-cols-1 md:grid-cols-[1fr_auto]
                             gap-3 items-start md:items-center">
                              <div class="min-w-0">
                                <p class="text-sm font-medium text-gray-800">
                                  Sticky Question Source
                                </p>
                                <p class="text-xs text-gray-500 break-words">
                                  Where to pull sticky questions from (independent of rotation source)
                                </p>
                              </div>
                              <div class="w-full md:w-auto md:min-w-[14rem]">
                                <Dropdown
                                  v-model="stickyQuestionSourceComputed"
                                  :options="questionSourceOptions"
                                  option-label="label"
                                  option-value="value"
                                  class="w-full"
                                  placeholder="Select source"
                                  :pt="{ root: { class: 'w-full' } }"
                                />
                              </div>
                            </div>

                            <!-- Sticky Questions Count -->
                            <div class="grid grid-cols-1 md:grid-cols-[1fr_auto]
                             gap-3 items-start md:items-center">
                              <div class="min-w-0">
                                <p class="text-sm font-medium text-gray-800">
                                  Sticky Questions Count
                                </p>
                                <p class="text-xs text-gray-500 break-words">
                                  How many sticky-flagged questions to include
                                  (on top of rotation count)
                                </p>
                              </div>
                              <div class="w-full md:w-auto md:min-w-[8rem]">
                                <InputNumber
                                  v-model="localStickyQuestionsCount"
                                  :min="0"
                                  :max="50"
                                  showButtons
                                  buttonLayout="horizontal"
                                  class="w-full"
                                  :pt="{ root: { class: 'w-full' } }"
                                  fluid
                                />
                              </div>
                            </div>

                            <!-- Warning: no sticky questions found -->
                            <div v-if="availableStickyQuestionsCount === 0"
                             class="p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p class="text-sm text-red-700">
                                <i class="pi pi-exclamation-triangle mr-1"></i>
                                No questions are flagged as sticky in the
                                <strong>{{ stickyQuestionSourceComputed === 'dimensions'
                                  ? 'assigned dimensions'
                                  : 'schema questions' }}</strong>.
                                Mark questions as sticky in the question editor first.
                              </p>
                            </div>

                            <!-- Warning: count exceeds available -->
                            <div v-else-if="localStickyQuestionsCount > availableStickyQuestionsCount"
                             class="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p class="text-sm text-amber-700">
                                <i class="pi pi-info-circle mr-1"></i>
                                You configured {{ localStickyQuestionsCount }} sticky questions
                                but only <strong>{{ availableStickyQuestionsCount }}</strong>
                                {{ availableStickyQuestionsCount === 1 ? 'is' : 'are' }}
                                flagged as sticky. All {{ availableStickyQuestionsCount }}
                                will be included.
                              </p>
                            </div>

                            <!-- Info: valid sticky setup -->
                            <div v-else-if="localStickyQuestionsCount > 0
                             && availableStickyQuestionsCount >= localStickyQuestionsCount"
                             class="p-3 bg-green-50 border border-green-200 rounded-lg">
                              <p class="text-sm text-green-700">
                                <i class="pi pi-check-circle mr-1"></i>
                                {{ localStickyQuestionsCount }} of
                                {{ availableStickyQuestionsCount }}
                                available sticky questions will be included in every rotation.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div class="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p class="text-sm text-green-700">
                            <i class="pi pi-check-circle mr-1"></i>
                            Questions will be randomly selected from
                             your question pool for each new assessment.
                          </p>
                        </div>

                        <!-- Question Rotation Save Button -->
                        <div class="mt-4 flex justify-end">
                <Button
                            label="Save Question Rotation Settings"
                            icon="pi pi-save"
                            @click="saveQuestionRotationSettings"
                            class="px-6"
                            :loading="savingQuestionRotation"
                            :disabled="!hasQuestionRotationChanges"
                          />
                        </div>
              </div>
            </div>
          </div>
        </div>

                <!-- First Login Task Configuration -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-sign-in mr-2 text-indigo-600"></i>
                      First Login Task
                    </h4>
                  </div>
                  <div class="p-6">
                    <div class="space-y-4">
                      <div class="flex items-center justify-between py-3 border-b border-gray-100">
                        <div>
                          <p class="text-sm font-medium text-gray-800">Enable First Login Task</p>
                          <p class="text-xs text-gray-500">
                            Mark this assessment as a task to be assigned on first login
                          </p>
                        </div>
                        <Checkbox
                          v-model="isFirstLoginTaskComputed"
                          :binary="true"
                        />
                      </div>

                      <div v-if="isFirstLoginTaskComputed"
                        class="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <p class="text-sm text-indigo-700">
                          <i class="pi pi-info-circle mr-1"></i>
                          This assessment will be automatically assigned to users
                          on their first login.
                        </p>
                      </div>

                      <div v-else class="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p class="text-sm text-gray-600">
                          <i class="pi pi-info-circle mr-1"></i>
                          Enable first login task to automatically assign this
                          assessment to new users.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Level System Configuration -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-star mr-2 text-yellow-600"></i>
                      Level System
                    </h4>
                  </div>
                  <div class="p-6 overflow-hidden">
                    <div class="space-y-4 min-w-0">
                      <div class="flex items-center justify-between py-3 border-b border-gray-100">
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-medium text-gray-800">Enable Level System</p>
                          <p class="text-xs text-gray-500 break-words">
                            Assign levels to users based on assessment performance
                          </p>
                        </div>
                        <Checkbox
                          v-model="levelSystemEnabledComputed"
                          :binary="true"
                          class="flex-shrink-0 ml-4"
                        />
                      </div>

                      <div v-if="levelSystemEnabledComputed" class="mt-4 space-y-3">
                        <!-- Development points per completion -->
                        <div class="py-3 border-b border-gray-100">
                          <div class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3
                           items-start md:items-center">
                            <div class="min-w-0">
                              <p class="text-sm font-medium text-gray-800">
                                Development points per completion</p>
                              <p class="text-xs text-gray-500 break-words">
                                Development points awarded for completing this assessment
                              </p>
                            </div>
                            <div class="w-full md:w-auto md:min-w-[8rem]">
                              <InputNumber
                                v-model="localXpPerCompletion"
                                :min="0"
                                :max="1000"
                                showButtons
                                buttonLayout="horizontal"
                                class="w-full"
                                :pt="{ root: { class: 'w-full' } }"
                                fluid
                              />
                            </div>
                          </div>
                        </div>

                        <div class="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p class="text-sm text-yellow-700">
                            <i class="pi pi-star mr-1"></i>
                            Users will be assigned levels based on
                             their assessment scores and performance.
             </p>
           </div>

                        <!-- Level System Save Button -->
                        <div class="mt-4 flex justify-end">
             <Button
                            label="Save Level System Settings"
                            icon="pi pi-save"
                            @click="saveLevelSystemSettings"
                            class="px-6"
                            :loading="savingLevelSystem"
                            :disabled="!hasLevelSystemChanges"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Admin Creation Configuration -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-users mr-2 text-green-600"></i>
                      Admin Creation Settings
                    </h4>
                  </div>
                  <div class="p-6">
                    <div class="flex items-center justify-between py-3">
                      <div>
                        <p class="text-sm font-medium text-gray-800">
                          Available for Admin Creation
                        </p>
                        <p class="text-xs text-gray-500">
                          Show this schema in User Administration dropdown for creating assessments
                        </p>
                      </div>
                      <Checkbox
                        v-model="canBeCreatedByAdminComputed"
                        :binary="true"
                      />
                    </div>
                  </div>
                </div>

                <!-- External Evaluation Configuration (hidden for external evaluation schemas) -->
                <div
                  v-if="!currentAssessmentSchema.isExternalEvaluationSchema"
                  class="bg-white border border-gray-200 rounded-lg shadow-sm"
                >
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-link mr-2 text-indigo-600"></i>
                      External Evaluation
                    </h4>
                  </div>
                  <div class="p-6">
                    <div class="space-y-4">
                      <div class="flex items-center justify-between py-3 border-b border-gray-100">
                        <div>
                          <p class="text-sm font-medium text-gray-800">
                            Allow External Evaluation
                          </p>
                          <p class="text-xs text-gray-500">
                            Enable this schema to create external evaluations using another schema
                          </p>
                        </div>
                        <Checkbox
                          v-model="canCreateExternalEvaluationComputed"
                          :binary="true"
                        />
                      </div>

                      <!-- External Evaluation Schema Selection -->
                      <div v-if="externalEvaluationCanCreateForDisplay"
                        class="mt-4 space-y-4">

                        <!-- Option 1: Create New Schema -->
                        <div>
                          <Button
                            label="Create New External Evaluation Schema"
                            icon="pi pi-plus"
                            class="w-full"
                            severity="secondary"
                            @click="openCreateExternalSchemaDialog"
                          />
                          <p class="text-xs text-gray-500 mt-2 text-center">
                            Creates a new schema with type: <code class="bg-gray-100 px-1 rounded">
                                {{ generatedExternalSchemaType }}</code>
                          </p>
                        </div>

                        <!-- OR Separator -->
                        <div class="flex items-center gap-4">
                          <div class="flex-1 border-t border-gray-300"></div>
                          <span class="text-sm text-gray-500 font-medium">OR</span>
                          <div class="flex-1 border-t border-gray-300"></div>
                        </div>

                        <!-- Option 2: Select Existing Schema -->
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-2">
                            Select Existing Schema
                          </label>
                          <Dropdown
                            v-model="selectedExternalEvaluationSchemaId"
                            :options="availableSchemasForExternalEvaluation"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Choose an existing schema..."
                            class="w-full"
                          />
                          <p class="text-xs text-gray-500 mt-2">
                            The selected schema will be marked as an external evaluation
                            schema and its dimensions will be cleared.
                          </p>
                        </div>

                        <!-- Error: Linked schema not found -->
                        <div v-if="externalEvaluationSchemaIdForDisplay
                          && externalEvaluationSchemaNotFound"
                             class="p-3 bg-red-50 border border-red-300 rounded-lg">
                          <p class="text-sm text-red-700 font-medium mb-2">
                            <i class="pi pi-exclamation-triangle mr-1"></i>
                            External Evaluation Schema Not Found
                          </p>
                          <p class="text-xs text-red-600">
                            The linked schema
                            (ID: {{ externalEvaluationSchemaIdForDisplay }})
                            could not be found. It may have been deleted. Please select a new
                            schema or create a new one.
                          </p>
                        </div>

                        <!-- Info about current external evaluation schema if already set -->
                        <div v-else-if="externalEvaluationSchemaIdForDisplay
                          && !externalEvaluationSchemaNotFound"
                             class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p class="text-sm text-blue-700">
                            <i class="pi pi-info-circle mr-1"></i>
                            Currently linked to external evaluation schema:
                            <strong>{{ externalEvaluationSchemaName }}</strong>
                          </p>
                        </div>

                        <!-- Save Button (for dropdown selection) -->
                        <div class="flex justify-end mt-4">
                          <Button
                            label="Save"
                            icon="pi pi-save"
                            class="p-button-primary"
                            @click="saveExternalEvaluationSettings"
                            :disabled="!selectedExternalEvaluationSchemaId"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- External Evaluation Assessment Configuration -->
                <div
                  v-if="currentAssessmentSchema.isExternalEvaluationSchema"
                  class="bg-white border border-gray-200 rounded-lg shadow-sm"
                >
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-envelope mr-2 text-teal-600"></i>
                      External Evaluation Assessment Configuration
                    </h4>
                  </div>
                  <div class="p-6">
                    <div class="space-y-4">
                      <div class="flex items-center justify-between py-3 border-b border-gray-100">
                        <div>
                          <p class="text-sm font-medium text-gray-800">
                            Display External Emails Input
                          </p>
                          <p class="text-xs text-gray-500">
                            Allow users to invite external participants via email
                            when creating evaluations with this schema
                          </p>
                        </div>
                        <Checkbox
                          v-model="canDisplayExternalEmailsComputed"
                          :binary="true"
                        />
                      </div>

                      <div v-if="currentAssessmentSchema.canDisplayExternalEmails"
                        class="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                        <p class="text-sm text-teal-700">
                          <i class="pi pi-info-circle mr-1"></i>
                          Users will be able to invite external participants via email
                          when creating evaluations using this schema.
                        </p>
                      </div>

                      <div v-else class="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p class="text-sm text-gray-600">
                          <i class="pi pi-info-circle mr-1"></i>
                          External email invitations are disabled for this schema.
                        </p>
                      </div>

                      <!-- Participant Configuration -->
                      <div class="border-t border-gray-200 pt-4 mt-4">
                        <h5 class="text-sm font-medium text-gray-800 mb-4">Participant Limits</h5>
                        <div class="grid grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                              Minimum Participants
                            </label>
                            <InputNumber
                              v-model="localMinParticipants"
                              :min="1"
                              :max="100"
                              class="w-full"
                              placeholder="Min participants"
                            />
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                              Maximum Participants
                            </label>
                            <InputNumber
                              v-model="localMaxParticipants"
                              :min="1"
                              :max="100"
                              class="w-full"
                              placeholder="Max participants"
                            />
                          </div>
                        </div>
                        <Button
                          label="Save Participant Limits"
                          icon="pi pi-save"
                          @click="saveParticipantLimits"
                          class="w-full mt-4"
                          :disabled="!hasParticipantLimitsChanged"
                        />
                      </div>

                      <!-- Extension length (FA-08.09; FA-08.39 not applied) -->
                      <div
                        class="border-t border-gray-200 pt-4 mt-4"
                        data-testid="ee-extension-days-section"
                      >
                        <h5 class="text-sm font-medium text-gray-800 mb-2">Deadline Extension</h5>
                        <p class="text-xs text-gray-500 mb-4">
                          Days added to open and began deadlines each time the creator extends.
                          Extension is allowed anytime while open responses remain and the deadline
                          is not expired (no 8-week / 3-day limit).
                        </p>
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-2">
                            Extension Days
                          </label>
                          <InputNumber
                            v-model="localExtensionDays"
                            :min="1"
                            :max="365"
                            class="w-full"
                            placeholder="Default 14"
                            data-testid="ee-extension-days"
                          />
                        </div>
                        <Button
                          label="Save Extension Days"
                          icon="pi pi-save"
                          data-testid="ee-extension-days-save"
                          @click="saveExtensionDays"
                          class="w-full mt-4"
                          :disabled="!hasExtensionDaysChanged"
                        />
                      </div>

                      <!-- Recreation interval (hybrid FA-08.32a / FA-08.33) -->
                      <div
                        class="border-t border-gray-200 pt-4 mt-4"
                        data-testid="ee-recreation-interval-section"
                      >
                        <h5 class="text-sm font-medium text-gray-800 mb-2">Recreation Interval</h5>
                        <p class="text-xs text-gray-500 mb-4">
                          After a cycle ends, the schema grant is revoked then may be auto-restored
                          so the creator can start again without an admin re-grant.
                          Admin re-grant always unlocks immediately.
                        </p>
                        <div class="flex items-center justify-between py-3 border-b border-gray-100 mb-4">
                          <div>
                            <p class="text-sm font-medium text-gray-800">
                              Enable immediately after cycle
                            </p>
                            <p class="text-xs text-gray-500">
                              When enabled, interval days are ignored and the grant is restored
                              as soon as the cycle completes.
                            </p>
                          </div>
                          <Checkbox
                            v-model="localRecreationImmediateAfterCycle"
                            :binary="true"
                            data-testid="ee-recreation-immediate-checkbox"
                          />
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-2">
                            Recreation Interval Days
                          </label>
                          <InputNumber
                            v-model="localRecreationIntervalDays"
                            :min="0"
                            :max="3650"
                            class="w-full"
                            :disabled="localRecreationImmediateAfterCycle"
                            placeholder="0 = admin re-grant only"
                            data-testid="ee-recreation-interval-days"
                          />
                          <p
                            v-if="!localRecreationImmediateAfterCycle"
                            class="text-xs text-gray-500 mt-1"
                          >
                            0 = admin must re-grant after cycle end; 1+ = auto-restore after that many days.
                          </p>
                        </div>
                        <Button
                          label="Save Recreation Interval"
                          icon="pi pi-save"
                          data-testid="ee-recreation-interval-save"
                          @click="saveRecreationIntervalDays"
                          class="w-full mt-4"
                          :disabled="!hasRecreationIntervalSettingsChanged"
                        />
                      </div>

                    </div>
                  </div>
                </div>

              </div>

            </div>
            </div>
          </TabPanel>
          <TabPanel value="2">
            <!-- Intro Text tab -->
            <div class="tab-content-wrapper">
              <div class="p-6" v-if="currentAssessmentSchema">
                <div class="mb-6">
                  <h3 class="text-2xl font-semibold text-gray-800 mb-2">Intro Text</h3>
                  <p class="text-gray-600">
                    Configure the intro page shown to users before they start this assessment.
                    Leave optional sections empty to hide them.
                  </p>
                </div>

                <div class="flex items-center justify-between py-3 border-b border-gray-200 mb-6">
                  <div>
                    <p class="text-sm font-medium text-gray-800">
                      Show intro page before assessment starts</p>
                    <p class="text-xs text-gray-500">
                      When enabled, users see this intro page before the questionnaire</p>
                  </div>
                  <Checkbox
                    v-model="introEnabledComputed"
                    :binary="true"
                    class="flex-shrink-0 ml-4"
                  />
                </div>

                <template v-if="localIntroConfig.enabled">
                  <!-- Language switcher -->
                  <div class="flex gap-2 mb-4">
                    <Button
                      :label="'English'"
                      :severity="introLang === 'en' ? 'primary' : 'secondary'"
                      @click="introLang = 'en'"
                    />
                    <Button
                      :label="'Deutsch'"
                      :severity="introLang === 'de' ? 'primary' : 'secondary'"
                      @click="introLang = 'de'"
                    />
                  </div>

                  <!-- Hidden chars (same pattern as knowledge database content editor) -->
                  <div
                    v-if="introRichTextHiddenCharsAggregate.found"
                    class="flex items-start gap-2 mb-4 p-3 bg-red-50 border border-red-300 rounded-lg text-sm text-red-800"
                  >
                    <i class="pi pi-exclamation-triangle mt-0.5 text-red-600"></i>
                    <div class="flex-1">
                      <span class="font-semibold">{{ introRichTextHiddenCharsAggregate.count }} hidden character{{ introRichTextHiddenCharsAggregate.count > 1 ? 's' : '' }} detected</span>
                      <span class="text-red-600"> ({{ introRichTextHiddenCharsAggregate.types.join(', ') }})</span>
                      <span> &mdash; in introduction, instructions, or additional info. These may cause broken words or unexpected line breaks.</span>
                    </div>
                    <Button
                      label="Clean"
                      icon="pi pi-eraser"
                      size="small"
                      severity="danger"
                      @click="cleanIntroRichTextHiddenChars"
                    />
                  </div>
                  <div
                    v-else-if="introShowHiddenCharsOkBanner"
                    class="flex items-center gap-2 mb-4 p-2 bg-green-50 border border-green-300 rounded-lg text-sm text-green-800"
                  >
                    <i class="pi pi-check-circle text-green-600"></i>
                    <span>No hidden characters detected in intro rich text fields.</span>
                  </div>

                  <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <!-- Form fields -->
                    <div class="space-y-6">
                      <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                        <h4 class="text-lg font-medium text-gray-800 mb-4 flex items-center">
                          <i class="pi pi-book mr-2 text-teal-600"></i>
                          Section 1 — Main card
                        </h4>
                        <div class="space-y-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                              Page Heading</label>
                            <InputText
                              v-model="localIntroConfig.title[introLang]"
                              class="w-full"
                              placeholder="e.g. Welcome to this assessment"
                            />
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                              Introduction content (paragraphs, lists, formatted text)</label>
                            <Editor
                              v-model="localIntroConfig.content[introLang]"
                              editorStyle="height: 180px;"
                              class="intro-editor"
                            />
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                              Start Button Text</label>
                            <InputText
                              v-model="localIntroConfig.buttonText[introLang]"
                              class="w-full"
                              :placeholder="introLang === 'en' ? 'Let\'s start' : 'Los geht\'s'"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                        <h4 class="text-lg font-medium text-gray-800 mb-4">
                          Section 2 — Instructions (optional)</h4>
                        <div class="space-y-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                              Instructions Section Heading</label>
                            <InputText
                              v-model="localIntroConfig.instructionsHeading[introLang]"
                              class="w-full"
                              placeholder="e.g. How it works"
                            />
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                              Instructions (use bullet list for step-by-step items)</label>
                            <Editor
                              v-model="localIntroConfig.instructionsContent[introLang]"
                              editorStyle="height: 160px;"
                              class="intro-editor"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                        <h4 class="text-lg font-medium text-gray-800 mb-4">
                          Section 3 — Additional info (optional)</h4>
                        <div class="space-y-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                              Additional Information Heading</label>
                            <InputText
                              v-model="localIntroConfig.additionalInfoHeading[introLang]"
                              class="w-full"
                              placeholder="e.g. Definition"
                            />
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                              Additional information text</label>
                            <Editor
                              v-model="localIntroConfig.additionalInfoContent[introLang]"
                              editorStyle="height: 140px;"
                              class="intro-editor"
                            />
                          </div>
                        </div>
                      </div>

                      <Button
                        label="Save Intro Settings"
                        icon="pi pi-save"
                        @click="saveIntroSettings"
                        class="w-full"
                        :loading="savingIntroSettings"
                      />
                    </div>

                    <!-- Live Preview -->
                    <div class="bg-gray-50 border border-gray-200 rounded-lg shadow-sm p-6
                      sticky top-4">
                      <h4 class="text-sm font-medium text-gray-600 mb-3">
                        Preview ({{ introLang === 'en' ? 'English' : 'Deutsch' }})</h4>
                      <div class="bg-white border border-gray-200 rounded-lg p-4 text-lg
                        max-h-[70vh] overflow-y-auto">
                        <h2 class="text-xl font-semibold mb-2">
                          {{ localIntroConfig.title[introLang] || '(Page Heading)' }}</h2>
                        <div v-html="introPreviewMainContentHtml"
                          class="mb-4 prose prose-sm max-w-none"></div>
                        <button type="button" class="text-white bg-[var(--color-orange)]
                          rounded-full py-2 px-6 text-base" disabled>
                          {{ localIntroConfig.buttonText[introLang]
                            || (introLang === 'en' ? "Let's start" : "Los geht's") }}
                        </button>
                        <template v-if="localIntroConfig.instructionsContent[introLang]">
                          <h2 class="text-lg font-semibold mt-6 mb-2">
                            {{ localIntroConfig.instructionsHeading[introLang]
                            || 'How it works' }}</h2>
                          <div v-html="introPreviewInstructionsHtml"
                            class="mb-4 prose prose-sm max-w-none"></div>
                        </template>
                        <template v-if="localIntroConfig.additionalInfoContent[introLang]">
                          <h4 class="text-base font-semibold mt-6 mb-2">
                            {{ localIntroConfig.additionalInfoHeading[introLang]
                            || 'Additional info' }}</h4>
                          <div v-html="introPreviewAdditionalHtml"
                            class="prose prose-sm max-w-none"></div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </TabPanel>
          <TabPanel value="3">
            <!-- Questions content -->
            <div class="tab-content-wrapper">
              <div class="p-6" v-if="currentAssessmentSchema">
              <div class="mb-6">
                <h3 class="text-2xl font-semibold text-gray-800 mb-2">Question Management</h3>
                <p class="text-gray-600">
                  Manage questions for your assessment schema.
                  Add, edit, and organize questions by dimensions.
             </p>
           </div>

              <!-- Question Statistics -->
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-question-circle text-blue-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-blue-600 font-medium">Total Questions</p>
                      <p class="text-2xl font-bold text-blue-800">
                        {{ currentAssessmentSchema.assessmentQuestionArray?.length || 0 }}
                      </p>
                    </div>
                  </div>
           </div>

                <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-check-circle text-green-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-green-600 font-medium">Valid Questions</p>
                      <p class="text-2xl font-bold text-green-800">{{ validQuestionsCount }}</p>
                    </div>
                  </div>
                </div>

                <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-exclamation-triangle text-yellow-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-yellow-600 font-medium">Invalid Questions</p>
                      <p class="text-2xl font-bold text-yellow-800">
                        {{ getInvalidQuestions?.length || 0 }}
                      </p>
                    </div>
          </div>
        </div>

                <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-sitemap text-purple-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-purple-600 font-medium">Dimensions</p>
                      <p class="text-2xl font-bold text-purple-800">{{
                       currentAssessmentSchema.dimensions?.length || 0
                        }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Bar -->
              <div class="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div class="flex flex-wrap gap-2">
          <Button
                      label="Add New Question"
                      icon="pi pi-plus"
                      @click="openNewQuestionDialog"
                      class="p-button-primary"
                    />
                    <Button
                      label="Import from Excel"
            icon="pi pi-file-excel"
            data-testid="cms-import-excel"
            @click="openExcelImportDialog"
                      class="p-button-outlined"
                      severity="success"
          />
          <Button
            label="Smart Import from Excel"
                      icon="pi pi-magic-wand"
            @click="openSmartExcelImportDialog"
                      class="p-button-outlined"
                      severity="warning"
          />
          <Button
                      label="Import JSON"
                      icon="pi pi-file"
            @click="openJsonImportDialog"
                      class="p-button-outlined"
                      severity="info"
                    />
        </div>

                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-600">Filter by dimension:</span>
                    <Dropdown
                      v-model="selectedDimensionFilter"
                      :options="dimensionFilterOptions"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="All dimensions"
                      class="w-48"
                      @change="onDimensionFilterChange"
                    />
                  </div>
          </div>
        </div>

        <!-- Questions List -->
              <div class="bg-white border border-gray-200 rounded-lg flex flex-col"
               style="height: 500px;">
                <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                  <h4 class="text-lg font-medium text-gray-800 flex items-center">
                    <i class="pi pi-list mr-2 text-gray-600"></i>
                    Questions List
                    <span class="ml-2 text-sm text-gray-500">
                      ({{ filteredQuestions.length }} questions)
                    </span>
                  </h4>
                </div>

                <div class="p-6 flex-1 overflow-y-auto">
                  <div v-if="filteredQuestions.length > 0" class="space-y-4">
                    <div
                      v-for="(question, index) in filteredQuestions"
                :key="question._id || index"
                      class="border border-gray-200
                       rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <QuestionListItem
                  :question="question"
                  :index="index"
                  :is-expanded="expandedQuestionIndex === index"
                  :has-valid-dimension="validateQuestionDimension(question)"
                  :selected="question._id ? selectedQuestions.includes(question._id) : false"
                  :selectable="true"
                  @toggle-expanded="toggleQuestionExpanded(index)"
                  @toggle-selected="toggleQuestionSelection($event)"
                  @update-question="updateQuestion(index, $event)"
                  @save-new="saveQuestion($event)"
                  @delete-question="handleQuestionDelete(index)"
                  @cancel="collapseQuestion(index)"
                />
          </div>
          </div>

                  <div v-else class="text-center py-12">
                    <i class="pi pi-question-circle text-gray-400 text-6xl mb-4"></i>
                    <h3 class="text-lg font-medium text-gray-600 mb-2">No Questions Found</h3>
                    <p class="text-gray-500 mb-4">
                      {{ selectedDimensionFilter ?
                       'No questions found for the selected dimension.' :
                       'Start by adding your first question to this assessment schema.' }}
                    </p>
                    <Button
                      label="Add First Question"
                      icon="pi pi-plus"
                      @click="openNewQuestionDialog"
                      class="p-button-primary"
                    />
        </div>
      </div>
      </div>

              <!-- Bulk Actions -->
              <div v-if="filteredQuestions.length > 0"
                 class="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div class="flex items-center gap-2">
                    <Checkbox
                      v-model="selectAllQuestions"
                      :binary="true"
                    />
                    <span class="text-sm text-gray-600">Select all questions</span>
    </div>

                  <div v-if="selectedQuestions.length > 0"
                     class="flex flex-wrap items-center gap-2">
                    <SplitButton
                      label="Set Force Input"
                      icon="pi pi-pencil"
                      :model="bulkForceInputMenuItems"
                      class="p-button-outlined"
                    />
                    <SplitButton
                      label="Set Polarity"
                      icon="pi pi-tag"
                      :model="bulkPolarityMenuItems"
                      class="p-button-outlined"
                    />
                    <Button
                      :label="`Delete Selected (${selectedQuestions.length})`"
                      icon="pi pi-trash"
                      @click="confirmBulkDelete"
                      class="p-button-outlined"
                      severity="danger"
                    />
                    <Button
                      :label="`Export Selected (${selectedQuestions.length})`"
                      icon="pi pi-download"
                      @click="exportSelectedQuestions"
                      class="p-button-outlined"
                      severity="info"
                    />
                  </div>
                </div>
              </div>
            </div>
            </div>
          </TabPanel>
          <TabPanel value="4">
            <!-- Dimensions content -->
            <div class="tab-content-wrapper">
              <div class="p-6" v-if="currentAssessmentSchema">
              <div class="mb-6">
                <h3 class="text-2xl font-semibold text-gray-800 mb-2">Dimensions Management</h3>
                <p class="text-gray-600">
                  Configure and manage dimensions for your assessment schema.
                  Dimensions help organize questions and provide structured analysis.
                </p>
              </div>

              <!-- Dimensions Overview -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-sitemap text-blue-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-blue-600 font-medium">Total Dimensions</p>
                      <p class="text-2xl font-bold text-blue-800">
                        {{ currentAssessmentSchema.dimensions?.length || 0 }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-check-circle text-green-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-green-600 font-medium">Dimensions with Questions</p>
                      <p class="text-2xl font-bold text-green-800">
                        {{ dimensionsWithQuestionsCount }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-exclamation-triangle text-yellow-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-yellow-600 font-medium">Empty Dimensions</p>
                      <p class="text-2xl font-bold text-yellow-800">{{ emptyDimensionsCount }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Auto Dimensions Matching -->
              <div class="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
                <div class="px-6 py-4 border-b border-gray-200">
                  <h4 class="text-lg font-medium text-gray-800 flex items-center">
                    <i class="pi pi-magic-wand mr-2 text-blue-600"></i>
                    Auto Dimensions Matching
                  </h4>
                </div>

                <div class="p-6">
                  <!-- Show read-only message if external evaluation schema -->
                  <div
                    v-if="isDimensionsReadOnly"
                    class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                  >
                    <div class="flex items-center gap-2">
                      <i class="pi pi-lock text-yellow-600"></i>
                      <p class="text-sm text-yellow-800">
                        Auto dimension matching is disabled for external evaluation schemas.
                        Dimensions are managed by the parent schema.
                      </p>
                    </div>
                  </div>

                  <div class="mb-4">
                    <p class="text-gray-600 mb-4">
                      Automatically detect and match question dimensions with platform dimensions.
                      This will scan all questions and find exact matches with existing dimensions.
                    </p>

                    <div class="flex gap-3">
                      <Button
                        label="Scan Questions"
                        icon="pi pi-search"
                        @click="scanQuestionDimensions"
                        :loading="scanningDimensions"
                        :disabled="isDimensionsReadOnly"
                        class="p-button-primary"
                      />
                      <Button
                        v-if="dimensionMatches.filter(m => m.status === 'matched').length > 0"
                        label="Apply Matches"
                        icon="pi pi-check"
                        @click="applyDimensionMatches"
                        :loading="applyingMatches"
                        :disabled="isDimensionsReadOnly"
                        class="p-button-success"
                      />
                      <Button
                        v-if="dimensionMatches.length > 0"
                        label="Clear Matches"
                        icon="pi pi-times"
                        @click="clearDimensionMatches"
                        :disabled="isDimensionsReadOnly"
                        class="p-button-text"
                      />
                    </div>
                  </div>

                  <!-- Matching Results -->
                  <div v-if="dimensionMatches.length > 0" class="mt-6">
                    <h5 class="text-md font-medium text-gray-800 mb-4">Matching Results</h5>
                    <div class="space-y-3">
                      <div
                        v-for="match in dimensionMatches"
                        :key="match.questionDimension"
                        class="flex items-center justify-between p-3 border rounded-lg"
                        :class="{
                          'border-green-200 bg-green-50': match.status === 'matched',
                          'border-blue-200 bg-blue-50': match.status === 'already_assigned',
                          'border-red-200 bg-red-50': match.status === 'no_match'
                        }"
                      >
                        <div class="flex items-center">
                          <i
                            class="pi mr-3"
                            :class="{
                              'pi-check-circle text-green-600': match.status === 'matched',
                              'pi-check text-blue-600': match.status === 'already_assigned',
                              'pi-times-circle text-red-600': match.status === 'no_match'
                            }"
                          ></i>
                          <div>
                            <span class="font-medium">{{ match.questionDimension }}</span>
                            <span v-if="match.status === 'matched'" class="text-gray-600 ml-2">
                              → {{ match.platformDimension.translation?.en
                                || match.platformDimension.value }}
                            </span>
                            <span v-else-if="match.status === 'already_assigned'"
                             class="text-blue-600 ml-2">
                              ✓ Already assigned
                            </span>
                            <span v-else class="text-red-600 ml-2">No match found</span>
                          </div>
                        </div>
                        <div v-if="match.status === 'matched'
                         || match.status === 'already_assigned'"
                         class="text-sm text-gray-500">
                          {{ match.questionsCount }} question(s)
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- No Matches Message -->
                  <div v-else-if="scannedDimensions && dimensionMatches.length === 0" class="mt-6">
                    <div class="text-center py-8 text-gray-500">
                      <i class="pi pi-info-circle text-4xl mb-4 text-gray-300"></i>
                      <p class="text-lg font-medium">No dimension matches found</p>
                      <p class="text-sm mt-2">
                        All question dimensions need to be manually assigned
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Dimensions Manager -->
              <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div class="px-6 py-4 border-b border-gray-200">
                  <h4 class="text-lg font-medium text-gray-800 flex items-center">
                    <i class="pi pi-cog mr-2 text-gray-600"></i>
                    Dimensions Configuration
                  </h4>
                </div>

                <div class="p-6">
          <DimensionsManager
            :dimensions="currentAssessmentSchema.dimensions || []"
            :assessment-id="currentAssessmentSchema._id"
            :questions="currentAssessmentSchema.assessmentQuestionArray || []"
            :has-dimension-questions="hasDimensionQuestions"
            @dimensions-updated="refreshAssessmentSchema"
          />
        </div>
              </div>

            </div>
            </div>
          </TabPanel>
          <TabPanel value="5">
            <!-- Charts content -->
            <div class="tab-content-wrapper">
              <div class="p-6" v-if="currentAssessmentSchema">
              <div class="mb-6">
                <h3 class="text-2xl font-semibold text-gray-800 mb-2">Charts Configuration</h3>
                <p class="text-gray-600">
                  Configure charts and visualizations for your assessment schema.
                  Set up how assessment results will be displayed and analyzed.
                </p>
              </div>

              <!-- Charts Overview -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-chart-bar text-purple-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-purple-600 font-medium">Chart Types</p>
                      <p class="text-2xl font-bold text-purple-800">Multiple</p>
                    </div>
                  </div>
                </div>

                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-sitemap text-blue-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-blue-600 font-medium">Dimensions</p>
                      <p class="text-2xl font-bold text-blue-800">
                        {{ currentAssessmentSchema.dimensions?.length || 0 }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div class="flex items-center">
                    <i class="pi pi-check-circle text-green-600 text-2xl mr-3"></i>
                    <div>
                      <p class="text-sm text-green-600 font-medium">Status</p>
                      <p class="text-2xl font-bold text-green-800">Ready</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Charts Manager -->
              <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div class="px-6 py-4 border-b border-gray-200">
                  <h4 class="text-lg font-medium text-gray-800 flex items-center">
                    <i class="pi pi-chart-bar mr-2 text-gray-600"></i>
                    Charts Configuration
                  </h4>
                </div>

                <div class="p-6">
          <ChartsManager
            :dimensions="currentAssessmentSchema.dimensions || []"
            :assessment-id="currentAssessmentSchema._id"
          />
        </div>
      </div>

              <!-- Chart Preview Section -->
              <div class="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h4 class="text-lg font-medium text-gray-800 mb-4 flex items-center">
                  <i class="pi pi-eye mr-2 text-gray-600"></i>
                  Chart Preview
                </h4>
                <div class="text-center py-8">
                  <i class="pi pi-chart-line text-gray-400 text-6xl mb-4"></i>
                  <h3 class="text-lg font-medium text-gray-600 mb-2">Chart Preview</h3>
                  <p class="text-gray-500 mb-4">
                    Configure your charts above to see a preview of how they will appear to users.
                  </p>
                  <Button
                    label="Preview Charts"
                    icon="pi pi-eye"
                    class="p-button-outlined"
                    severity="info"
                  />
                </div>
              </div>
            </div>
            </div>
          </TabPanel>
          <TabPanel value="6">
            <!-- Developer Settings content -->
            <div class="tab-content-wrapper">
              <div class="p-6" v-if="currentAssessmentSchema">
              <div class="mb-6">
                <h3 class="text-2xl font-semibold text-gray-800 mb-2">Developer Settings</h3>
                <p class="text-gray-600">
                  Development tools and testing utilities for assessment schema management.
                  <span class="text-orange-600 font-medium">
                    Use with caution in production environments.
                  </span>
                </p>
    </div>

              <!-- Development Tools Grid -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

                <!-- Test Data Generation -->
                <div class="bg-orange-50 border border-orange-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-orange-200">
                    <h4 class="text-lg font-medium text-orange-800 flex items-center">
                      <i class="pi pi-database mr-2"></i>
                      Test Data Generation
                    </h4>
                  </div>
                  <div class="p-6">
                    <div class="space-y-4">
                      <div class="p-4 bg-orange-100 border border-orange-300 rounded-lg">
                        <p class="text-sm text-orange-800">
                          <i class="pi pi-exclamation-triangle mr-2"></i>
                          <strong>Warning:</strong>
                           This will generate fake assessment data for testing purposes only.
                        </p>
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          Number of Test Records
                        </label>
                        <InputNumber
                          v-model="testDataCount"
                          :min="1"
                          :max="1000"
                          class="w-full"
                          placeholder="Enter number of test records"
                        />
                        <p class="text-xs text-gray-500 mt-1">
                          Generate fake assessment data for the last 3 months (weekly basis)
                        </p>
                      </div>

                      <Button
                        label="Generate Test Data"
                        icon="pi pi-database"
                        @click="showGenerateTestDataDialog"
                        class="w-full"
                        :loading="generatingTestData"
                        severity="warning"
                      />
                    </div>
                  </div>
                </div>

                <!-- Schema Validation -->
                <div class="bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-blue-200">
                    <h4 class="text-lg font-medium text-blue-800 flex items-center">
                      <i class="pi pi-check-circle mr-2"></i>
                      Schema Validation
                    </h4>
                  </div>
                  <div class="p-6">
                    <div class="space-y-4">
                      <div class="p-4 bg-blue-100 border border-blue-300 rounded-lg">
                        <p class="text-sm text-blue-800">
                          <i class="pi pi-info-circle mr-2"></i>
                          Validate the structure and integrity of your assessment schema.
                        </p>
                      </div>

                      <div class="space-y-2">
                        <div class="flex items-center justify-between
                         py-2 border-b border-blue-100">
                          <span class="text-sm text-gray-600">Schema Structure</span>
                          <i class="pi pi-check text-green-600"></i>
                        </div>
                        <div class="flex items-center justify-between
                         py-2 border-b border-blue-100">
                          <span class="text-sm text-gray-600">Question Validation</span>
                          <i class="pi pi-check text-green-600"></i>
                        </div>
                        <div class="flex items-center justify-between py-2">
                          <span class="text-sm text-gray-600">Dimension Mapping</span>
                          <i class="pi pi-check text-green-600"></i>
                        </div>
                      </div>

                      <Button
                        label="Validate Schema Structure"
                        icon="pi pi-check-circle"
                        @click="validateSchemaStructure"
                        class="w-full"
                        severity="info"
                      />
                    </div>
                  </div>
                </div>

                <!-- Assessment Creation Tools -->
                <div class="bg-green-50 border border-green-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-green-200">
                    <h4 class="text-lg font-medium text-green-800 flex items-center">
                      <i class="pi pi-users mr-2"></i>
                      Assessment Creation
                    </h4>
                  </div>
                  <div class="p-6">
                    <div class="space-y-4">
                      <div class="p-4 bg-green-100 border border-green-300 rounded-lg">
                        <p class="text-sm text-green-800">
                          <i class="pi pi-lightbulb mr-2"></i>
                          Create assessments for all users or specific test scenarios.
                        </p>
                      </div>

                      <div class="space-y-3">
                        <Button
                          label="Create Assessment for ALL Users"
                          icon="pi pi-users"
                          @click="createAssessmentFromSchema"
                          class="w-full"
                          :loading="creatingAssessment"
                          severity="success"
                        />

                        <Button
                          label="Create Test Assessment"
                          icon="pi pi-user"
                          @click="createTestAssessment"
                          class="w-full"
                          severity="secondary"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- System Information -->
                <div class="bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h4 class="text-lg font-medium text-gray-800 flex items-center">
                      <i class="pi pi-info-circle mr-2"></i>
                      System Information
                    </h4>
                  </div>
                  <div class="p-6">
                    <div class="space-y-3">
                      <div class="flex justify-between items-center py-2 border-b border-gray-100">
                        <span class="text-sm text-gray-600">Schema ID</span>
                        <span class="text-sm font-mono text-gray-800">
                          {{ currentAssessmentSchema._id }}
                        </span>
                      </div>
                      <div class="flex justify-between items-center py-2 border-b border-gray-100">
                        <span class="text-sm text-gray-600">Schema Type</span>
                        <span class="text-sm text-gray-800">
                          {{ currentAssessmentSchema.type }}
                        </span>
                      </div>
                      <div class="flex justify-between items-center py-2 border-b border-gray-100">
                        <span class="text-sm text-gray-600">Total Questions</span>
                        <span class="text-sm text-gray-800">
                          {{ currentAssessmentSchema.assessmentQuestionArray?.length || 0 }}
                        </span>
                      </div>
                      <div class="flex justify-between items-center py-2">
                        <span class="text-sm text-gray-600">Dimensions</span>
                        <span class="text-sm text-gray-800">
                          {{ currentAssessmentSchema.dimensions?.length || 0 }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Development Warning -->
              <div class="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div class="flex items-start">
                  <i class="pi pi-exclamation-triangle text-red-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <h4 class="text-red-800 font-medium mb-2">Development Environment Notice</h4>
                    <p class="text-red-700 text-sm">
                      These tools are designed for development and testing purposes only.
                      <strong>
                        Never use these features in production environments
                      </strong> as they may:
                    </p>
                    <ul class="text-red-700 text-sm mt-2 ml-4 list-disc">
                      <li>Generate fake data that could affect analytics</li>
                      <li>Create test assessments for real users</li>
                      <li>Modify production database records</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </TabPanel>
          <!-- Schema Templates tab hidden - templates are now managed at tenant level -->
          <!-- <TabPanel value="6">
            <div class="tab-content-wrapper">
              <SchemaTemplatesEditor
                v-if="currentAssessmentSchema"
                :schema-id="currentAssessmentSchema._id"
                :templates="schemaTemplates"
                @save="handleSaveTemplates"
                @cancel="handleCancelTemplates"
              />
            </div>
          </TabPanel> -->
      </TabPanels>
    </Tabs>

    <!-- Dialogs -->
    <Dialog
      header="New Question"
      v-model:visible="newQuestionDialogVisible"
      modal
      @hide="closeNewQuestionDialog"
    >
      <QuestionEditor
        :question="newQuestion"
        :is-new="true"
        @save-new="addNewQuestion"
        @cancel="closeNewQuestionDialog"
      />
    </Dialog>

    <Dialog
      header="Import Questions from Excel"
      v-model:visible="excelImportDialogVisible"
      modal
      @hide="closeExcelImportDialog"
    >
      <div class="p-4 space-y-4">
        <p>Drag & drop your Excel file or click to select.</p>
        <input
          type="file"
          accept=".xlsx, .xls"
          data-testid="cms-question-excel-input"
          @change="onExcelFileChange"
        />
        <div class="flex justify-end space-x-2">
          <Button label="Cancel" class="p-button-secondary" @click="closeExcelImportDialog" />
        </div>
      </div>
    </Dialog>

    <Dialog
      header="Smart Import from Excel"
      v-model:visible="smartExcelImportDialogVisible"
      modal
      @hide="closeSmartExcelImportDialog"
    >
      <div class="p-4 space-y-4">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h4 class="text-blue-800 font-medium mb-2">
            <i class="pi pi-info-circle mr-2"></i>
            Smart Import Features
          </h4>
          <ul class="text-blue-700 text-sm space-y-1">
            <li>• Automatically detects existing questions and updates them</li>
            <li>• Prevents duplicate questions</li>
            <li>• Updates positive/negative flags and other properties</li>
            <li>• Bulk saves all changes efficiently</li>
          </ul>
        </div>
        <p class="text-gray-700">
          <strong>Upload Excel file:</strong> The system will
           intelligently merge with existing questions.
        </p>
        <input
          type="file"
          accept=".xlsx, .xls"
          @change="onSmartExcelFileChange"
          class="w-full p-2 border rounded"
        />
        <div class="flex justify-end space-x-2">
          <Button label="Cancel" class="p-button-secondary" @click="closeSmartExcelImportDialog" />
        </div>
      </div>
    </Dialog>

    <Dialog
      header="Import Questions from JSON"
      v-model:visible="jsonImportDialogVisible"
      modal
      @hide="closeJsonImportDialog"
    >
      <div class="p-4 space-y-4">
        <label class="block text-sm font-medium text-gray-700">Paste JSON here:</label>
        <textarea v-model="jsonImportText" class="w-full p-2 border rounded h-40"></textarea>
        <div class="flex justify-end space-x-2">
          <Button label="Cancel" class="p-button-secondary" @click="closeJsonImportDialog" />
          <Button label="Import" @click="importQuestionsFromJson" />
        </div>
      </div>
    </Dialog>

    <Dialog
      header="Edit Time Settings"
      v-model:visible="timeSettingsOverlayVisible"
      modal
      @hide="closeTimeSettingsOverlay"
      class="w-11/12 md:w-1/2"
    >
      <AssessmentSchemaTimeSettings
        v-model:modelExpirationTime="localExpirationTime"
        v-model:modelReminderTime="localReminderTime"
        v-model:modelLinkAfterDone="localLinkAfterAssessment"
        :link-after-assessment-options="linkAfterAssessmentOptionsFromConfig"
        :schema-name="currentSchemaNameForLinkTab"
      />
      <div class="flex justify-end mt-4">
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-secondary ml-2"
          @click="closeTimeSettingsOverlay"
        />
        <Button label="Save" icon="pi pi-check" @click="saveNewTimeSettings" />
      </div>
    </Dialog>

    <!-- Confirm Dialog for question deletion -->
    <ConfirmDialog />

    <!-- Local Delete Confirmation Dialog -->
    <Dialog
      header="Delete Question"
      v-model:visible="showDeleteConfirmation"
      modal
      :closable="true"
      :closeOnEscape="true"
      class="w-96"
    >
      <div class="p-4">
        <div class="flex items-center mb-4">
          <i class="pi pi-exclamation-triangle text-red-500 text-2xl mr-3"></i>
          <p class="text-gray-700">
            Are you sure you want to delete the question
            <strong>"{{ questionToDelete?.question?.properties?.content?.text_question?.en ||
                       questionToDelete?.question?.properties?.content?.text_question?.de ||
                       'Untitled Question' }}"</strong>?
          </p>
        </div>
        <p class="text-sm text-gray-500 mb-4">
          This action cannot be undone.
        </p>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button
            label="Cancel"
            class="p-button-secondary"
            @click="cancelDeleteQuestion"
          />
          <Button
            label="Delete"
            severity="danger"
            @click="confirmDeleteQuestion"
            :loading="isDeletingQuestion"
          />
        </div>
      </template>
    </Dialog>

    <!-- DevTips Results Overlay -->
    <DevTipsResultsOverlay
      v-model:visible="devTipsResultsOverlayVisible"
      :results="devTipsResults"
      :loading="devTipsLoading"
      :error="devTipsError"
      @close="closeDevTipsResultsOverlay"
    />

    <!-- Generate Test Data Warning Dialog -->
    <Dialog
      v-model:visible="generateTestDataDialogVisible"
      modal
      header="⚠️ DEVELOPMENT ONLY - Generate Test Data"
      :style="{ width: '600px' }"
      :closable="false"
    >
      <div class="confirmation-content">
        <div class="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <h3 class="text-orange-800 font-medium mb-2">
            <i class="pi pi-exclamation-triangle mr-2"></i>
            Development Environment Only
          </h3>
          <p class="text-orange-700 text-sm mb-3">
            <strong>This feature is for development and testing purposes only!</strong>
          </p>
          <ul class="text-orange-700 text-sm list-disc list-inside space-y-1">
            <li>Will generate fake assessment data for the last 3 months</li>
            <li>Creates weekly assessments with rotated questions</li>
            <li>Data will be created ONLY for your user account</li>
            <li>This should NEVER be triggered in production</li>
          </ul>
        </div>
        <p class="text-gray-700">
          Are you sure you want to generate test data?
           This will create multiple assessments with sample data.
        </p>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times"
         class="p-button-text" @click="closeGenerateTestDataDialog" />
        <Button label="Generate Test Data" icon="pi pi-database"
         class="p-button-warning" @click="confirmGenerateTestData" />
      </template>
    </Dialog>

    <!-- Test Reflection Guidelines Confirmation Dialog -->
    <Dialog
      v-model:visible="testReflectionGuidelinesDialogVisible"
      modal
      header="Test Reflection Guidelines"
      :style="{ width: '500px' }"
      :closable="false"
    >
      <div class="confirmation-content">
        <div class="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 class="text-blue-800 font-medium mb-2">
            <i class="pi pi-refresh mr-2"></i>
            Test Mode
          </h3>
          <p class="text-blue-700 text-sm">
            This will test the reflection guidelines configuration without actually triggering them.
          </p>
  </div>
        <p class="text-gray-700">
          Continue with the test?
        </p>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times"
         class="p-button-text" @click="testReflectionGuidelinesDialogVisible = false" />
        <Button label="Test" icon="pi pi-refresh"
         class="p-button-secondary" @click="confirmTestReflectionGuidelines" />
      </template>
    </Dialog>

    <!-- Trigger Reflection Guidelines Confirmation Dialog -->
    <Dialog
      v-model:visible="triggerReflectionGuidelinesDialogVisible"
      modal
      header="Trigger Reflection Guidelines"
      :style="{ width: '500px' }"
      :closable="false"
    >
      <div class="confirmation-content">
        <div class="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 class="text-green-800 font-medium mb-2">
            <i class="pi pi-paper-plane mr-2"></i>
            Immediate Trigger
          </h3>
          <p class="text-green-700 text-sm">
            This will immediately trigger reflection guidelines for this assessment schema.
          </p>
        </div>
        <p class="text-gray-700 mb-2">
          <strong>Warning:</strong>
          This action will create assessments for users
           using the mapped reflection guideline schema.
        </p>
        <p class="text-gray-700 text-sm mb-2">
          <i class="pi pi-info-circle mr-1"></i>
          The assessment will be created from the reflection guideline
           schema configured in the mapping, not from the current schema.
        </p>
        <p class="text-gray-700">
          Are you sure you want to proceed?
        </p>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times"
         class="p-button-text" @click="triggerReflectionGuidelinesDialogVisible = false" />
        <Button label="Trigger Now" icon="pi pi-paper-plane"
         class="p-button-success" @click="confirmTriggerReflectionGuidelines" />
      </template>
    </Dialog>

    <!-- External Evaluation Schema Change Warning Dialog -->
    <Dialog
      v-model:visible="externalEvaluationWarningDialogVisible"
      modal
      header="Warning: External Evaluation Schema Change"
      :style="{ width: '500px' }"
      :closable="false"
    >
      <div class="confirmation-content">
        <div class="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 class="text-yellow-800 font-medium mb-2">
            <i class="pi pi-exclamation-triangle mr-2"></i>
            Schema Change Warning
          </h3>
          <p class="text-yellow-700 text-sm">
            You are changing the external evaluation schema. If you proceed, the dimensions
            from the previous schema will be cleared and set to the new one.
          </p>
        </div>
        <p class="text-gray-700 mb-2">
          <strong>This action will:</strong>
        </p>
        <ul class="text-gray-700 text-sm mb-4 list-disc list-inside">
          <li>Clear dimensions from the previous external evaluation schema</li>
          <li>Set the new schema as the external evaluation schema</li>
          <li>Update the external evaluation configuration</li>
        </ul>
        <p class="text-gray-700">
          Are you sure you want to proceed?
        </p>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times"
         class="p-button-text" @click="externalEvaluationWarningDialogVisible = false" />
        <Button label="Okay" icon="pi pi-check"
         class="p-button-primary" @click="confirmSaveExternalEvaluationSettings" />
      </template>
    </Dialog>

    <!-- Disable External Evaluation Warning Dialog -->
    <Dialog
      v-model:visible="disableExternalEvaluationWarningDialogVisible"
      modal
      header="Warning: Disable External Evaluation"
      :style="{ width: '500px' }"
      :closable="false"
    >
      <div class="confirmation-content">
        <div class="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 class="text-yellow-800 font-medium mb-2">
            <i class="pi pi-exclamation-triangle mr-2"></i>
            Disable External Evaluation
          </h3>
          <p class="text-yellow-700 text-sm">
            You are disabling external evaluation. If you proceed, you will not be able
            to create external evaluations anymore.
          </p>
        </div>
        <p class="text-gray-700 mb-2">
          <strong>This action will:</strong>
        </p>
        <ul class="text-gray-700 text-sm mb-4 list-disc list-inside">
          <li>Disable external evaluation for this schema</li>
          <li>Clear the external evaluation schema link</li>
          <li>Remove the ability to create external evaluations</li>
        </ul>
        <p class="text-gray-700">
          Are you sure you want to proceed?
        </p>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times"
         class="p-button-text" @click="cancelDisableExternalEvaluation" />
        <Button label="Confirm" icon="pi pi-check"
         class="p-button-primary" @click="confirmDisableExternalEvaluation" />
      </template>
    </Dialog>

    <!-- Create External Evaluation Schema Dialog -->
    <Dialog
      v-model:visible="createExternalSchemaDialogVisible"
      modal
      header="Create External Evaluation Schema"
      :style="{ width: '550px' }"
      :closable="true"
    >
      <div class="space-y-4">
        <!-- Auto-generated Type Display -->
        <div class="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p class="text-sm text-gray-600">
            <i class="pi pi-tag mr-2"></i>
            Schema Type: <code class="bg-gray-200 px-2 py-1 rounded font-mono">
                {{ generatedExternalSchemaType }}</code>
          </p>
        </div>

        <!-- Name Fields -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Name (EN)</label>
            <InputText
              v-model="newExternalSchemaForm.nameEn"
              class="w-full"
              placeholder="English name"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Name (DE)</label>
            <InputText
              v-model="newExternalSchemaForm.nameDe"
              class="w-full"
              placeholder="German name"
            />
          </div>
        </div>

        <!-- Tags Field -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Tags (comma separated)</label>
          <InputText
            v-model="newExternalSchemaForm.tagsInput"
            class="w-full"
            placeholder="tag1, tag2, tag3"
          />
        </div>

        <!-- Question Rotation Settings -->
        <div class="border-t pt-4 mt-4">
          <h4 class="text-sm font-semibold text-gray-800 mb-3">Question Settings</h4>

          <!-- Rotated Questions Checkbox -->
          <div class="flex items-center mb-3">
            <Checkbox
              v-model="newExternalSchemaForm.rotatedQuestionsSchema"
              :binary="true"
              inputId="rotatedQuestions"
            />
            <label for="rotatedQuestions" class="ml-2 text-sm text-gray-700">
              Enable Question Rotation
            </label>
          </div>

          <!-- Rotation Period (only visible when rotation is enabled) -->
          <div v-if="newExternalSchemaForm.rotatedQuestionsSchema" class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Rotation Period</label>
            <Dropdown
              v-model="newExternalSchemaForm.rotationPeriod"
              :options="rotationPeriodOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
            />
          </div>

          <!-- Question Source -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Question Source</label>
            <div class="flex gap-4">
              <div class="flex items-center">
                <input
                  type="radio"
                  v-model="newExternalSchemaForm.questionSource"
                  value="direct"
                  id="source-direct"
                  class="mr-2"
                />
                <label for="source-direct" class="text-sm text-gray-700">Direct from Schema</label>
              </div>
              <div class="flex items-center">
                <input
                  type="radio"
                  v-model="newExternalSchemaForm.questionSource"
                  value="dimensions"
                  id="source-dimensions"
                  class="mr-2"
                />
                <label for="source-dimensions" class="text-sm text-gray-700">From Dimensions</label>
              </div>
            </div>
          </div>

          <!-- Total Questions -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Total Questions per Assessment</label>
            <InputNumber
              v-model="newExternalSchemaForm.totalQuestions"
              :min="1"
              :max="50"
              class="w-full"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeCreateExternalSchemaDialog"
        />
        <Button
          label="Create"
          icon="pi pi-check"
          class="p-button-primary"
          @click="handleCreateExternalSchema"
          :disabled="!newExternalSchemaForm.nameEn"
          :loading="creatingExternalSchema"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import MultiSelect from 'primevue/multiselect';
import Checkbox from 'primevue/checkbox';
import SplitButton from 'primevue/splitbutton';
import Dropdown from 'primevue/dropdown';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import QuestionEditor from '../components/QuestionEditor.vue';
import ReflexionGuidelineConfigForm from '../components/ReflexionGuidelineConfigForm.vue';
import { mapGetters, mapState, mapActions } from 'vuex';
import { USER_ROLES_ENUM_NAME } from '../../../services/enumService';
import QuestionListItem from '../components/QuestionListItem.vue';
import DevTipsResultsOverlay from './DevTipsResultsOverlay.vue';
import {
  parseExcelFile,
  processImportedQuestions,
  validateQuestion,
} from '../../../helpers/assessmentSchemasImportHelper';
import { formatWallClockSchedule } from '../../../helpers/scheduleTimezoneHelper';
import AssessmentSchemaTimeSettings from '../components/AssessmentSchemaTimeSettings.vue';
import DimensionsManager from '../components/DimensionsManager.vue';
import ChartsManager from '../components/ChartsManager.vue';
import Editor from 'primevue/editor';
import { sanitizeHiddenChars, detectHiddenChars, highlightHiddenChars } from '@/helpers/sanitizeText';
import {
  buildRecreationIntervalSavePayload,
  hasRecreationIntervalSettingsChanged as hasRecreationIntervalSettingsChangedHelper,
  validateRecreationIntervalForSave,
} from '@/helpers/externalEvaluationRecreation';

export default {
  name: 'AssessmentDetails',
  components: {
    Button,
    InputText,
    InputNumber,
    Dialog,
    ConfirmDialog,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    QuestionEditor,
    QuestionListItem,
    AssessmentSchemaTimeSettings,
    MultiSelect,
    Checkbox,
    SplitButton,
    DimensionsManager,
    ChartsManager,
    Dropdown,
    ReflexionGuidelineConfigForm,
    DevTipsResultsOverlay,
    Editor,
  },
  data() {
    return {
      // Local values for input fields to prevent duplicate requests
      localTotalQuestions: 6,
      localStickyQuestionsCount: 0,
      localXpPerCompletion: 0,
      localMinParticipants: 7,
      localMaxParticipants: 10,
      localExtensionDays: 14,
      localRecreationImmediateAfterCycle: false,
      localRecreationIntervalDays: 0,
      localMinAssessmentsForCharts: 5,
      introLang: 'en',
      savingIntroSettings: false,
      localIntroConfig: {
        enabled: false,
        title: { en: '', de: '' },
        content: { en: '', de: '' },
        buttonText: { en: '', de: '' },
        instructionsHeading: { en: '', de: '' },
        instructionsContent: { en: '', de: '' },
        additionalInfoHeading: { en: '', de: '' },
        additionalInfoContent: { en: '', de: '' },
      },
      // The task being edited. In a real app you would load it from an API or the store.
      isNameAvailable: false,
      currentSchemaName: {
        en: '',
        de: '',
      },
      newQuestionDialogVisible: false,
      excelImportDialogVisible: false,
      smartExcelImportDialogVisible: false,
      jsonImportDialogVisible: false,
      timeSettingsOverlayVisible: false,
      openApplyClassAndTypeDialogVisible: false,
      jsonImportText: '',
      errorLogs: [],
      // New question template; adjust initial values as needed.
      newQuestion: {
        properties: {
          dimensionName: '',
          dimension: '',
          content: {
            text_questionProgress: { en: '', de: '' },
            text_question: { en: '', de: '' },
          },
          options: { forceInput: false },
          data: {},
        },
        class: '',
        type: '',
        viewTimeLogs: [],
      },
      globalSelectedQuestionsClass: '',
      globalSelectedQuestionsType: '',
      localExpirationTime: 0,
      localReminderTime: 0,
      localLinkAfterAssessment: 'https://example.com/next',
      roleSelection: [], // local reactive copy of user roles
      isDeletingQuestion: false, // flag to prevent double deletion
      showDeleteConfirmation: false, // local confirmation dialog
      questionToDelete: null, // question to be deleted
      expandedQuestionIndex: -1, // track which question is expanded
      generateTestDataDialogVisible: false, // test data generation dialog
      generatingTestData: false, // loading state for test data generation
      savingQuestionRotation: false, // loading state for question rotation saving
      savingLevelSystem: false, // loading state for level system saving

      // External Evaluation Settings
      selectedExternalEvaluationSchemaId: null, // Selected external evaluation schema ID
      availableSchemasForExternalEvaluation: [], // List of schemas for dropdown
      externalEvaluationSchemaName: '', // Name of current external evaluation schema (if set)
      previousExternalEvaluationSchemaId: null, // Track previous selection for cleanup
      localCanCreateExternalEvaluation: null, // Local state for checkbox (null = use schema value)

      // Questions Management
      selectedDimensionFilter: null, // filter questions by dimension
      selectedQuestions: [], // array of selected question IDs

      // DevTips Settings
      sendingDevTips: false,
      devTipsPeriodOptions: [
        // Note: 'hourly' removed - not supported in new structure (main cron runs hourly at :00)
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Yearly', value: 'yearly' },
      ],
      // Schedule settings for DevTips
      devTipsSchedule: {
        hour: 8,
        dayOfWeek: 1, // Monday
        dayOfMonth: 1,
        month: 0, // January (yearly)
      },

      // Reflection Guidelines Settings
      triggeringReflectionGuidelines: false,
      testReflectionGuidelinesDialogVisible: false,
      triggerReflectionGuidelinesDialogVisible: false,

      // External Evaluation Settings Dialog
      externalEvaluationWarningDialogVisible: false,
      disableExternalEvaluationWarningDialogVisible: false,

      // Create External Evaluation Schema Dialog
      createExternalSchemaDialogVisible: false,
      creatingExternalSchema: false,
      externalEvaluationSchemaNotFound: false, // Flag to track if linked schema is missing
      newExternalSchemaForm: {
        nameEn: '',
        nameDe: '',
        tagsInput: '',
        rotatedQuestionsSchema: false,
        rotationPeriod: 'weekly',
        questionSource: 'dimensions',
        totalQuestions: 6,
      },

      // Auto Dimensions Matching
      scanningDimensions: false,
      applyingMatches: false,
      scannedDimensions: false,
      dimensionMatches: [],

      // DevTips Results Overlay
      devTipsResultsOverlayVisible: false,
      devTipsResults: null,
      devTipsLoading: false,
      devTipsError: null,

      // Reflection Guideline Configuration
      reflexionGuidelineConfig: {},

      // Question Rotation Settings
      rotationPeriodOptions: [
        // Note: 'hourly' removed - not supported in new structure (main cron runs hourly at :00)
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Yearly', value: 'yearly' },
      ],
      // Schedule settings for Question Rotation
      questionRotationSchedule: {
        hour: 8,
        dayOfWeek: 1, // Monday
        dayOfMonth: 1,
      },
      // Day of week options for schedule
      dayOfWeekOptions: [
        { label: 'Sunday', value: 0 },
        { label: 'Monday', value: 1 },
        { label: 'Tuesday', value: 2 },
        { label: 'Wednesday', value: 3 },
        { label: 'Thursday', value: 4 },
        { label: 'Friday', value: 5 },
        { label: 'Saturday', value: 6 },
      ],
      monthOptions: [
        { label: 'January', value: 0 },
        { label: 'February', value: 1 },
        { label: 'March', value: 2 },
        { label: 'April', value: 3 },
        { label: 'May', value: 4 },
        { label: 'June', value: 5 },
        { label: 'July', value: 6 },
        { label: 'August', value: 7 },
        { label: 'September', value: 8 },
        { label: 'October', value: 9 },
        { label: 'November', value: 10 },
        { label: 'December', value: 11 },
      ],
      questionSourceOptions: [
        { label: 'Direct from Schema', value: 'direct' },
        { label: 'From Dimensions', value: 'dimensions' },
      ],

      // Assessment creation
      creatingAssessment: false,
    };
  },
  computed: {
    ...mapState('assessmentSchemas', ['currentAssessmentSchema']),
    ...mapState('config', { configData: 'data' }),
    linkAfterAssessmentOptionsFromConfig() {
      const opts = this.configData?.linkAfterAssessmentOptions;
      return Array.isArray(opts) ? opts : [];
    },
    /** Schema name used to append ?tab= when result dashboard is selected (in schema only) */
    currentSchemaNameForLinkTab() {
      return this.currentAssessmentSchema?.type;
    },
    ...mapGetters({
      getEnumByName: 'enums/getEnumTypeByName',
      getAssessmentSchemaById: 'assessmentSchemas/getAssessmentSchemaById',
      error: 'assessmentSchemas/error',
      // Schema templates are now managed at tenant level, not schema level
      // schemaTemplates: 'assessmentSchemas/schemaTemplates',
      // Validation getters
      validateQuestionDimension: 'assessmentSchemas/validateQuestionDimension',
      getQuestionsForDimension: 'assessmentSchemas/getQuestionsForDimension',
      hasDimensionQuestions: 'assessmentSchemas/hasDimensionQuestions',
      hasInvalidQuestions: 'assessmentSchemas/hasInvalidQuestions',
      hasUnassignedDimensions: 'assessmentSchemas/hasUnassignedDimensions',
      getInvalidQuestions: 'assessmentSchemas/getInvalidQuestions',
      getUnassignedDimensions: 'assessmentSchemas/getUnassignedDimensions',
    }),
    ...mapGetters('enums', { getEnumByName: 'getEnumTypeByName' }),
    ...mapGetters('dimensions', {
      getAllDimensions: 'getAllDimensions',
      getDimensionByValue: 'getDimensionByValue',
    }),
    currentSchemaNeedsMigration() {
      return this.schemaNeedsMigration(this.currentAssessmentSchema);
    },
    unmigratedBlocksList() {
      const blocks = this.getUnmigratedBlocks(this.currentAssessmentSchema);
      return blocks.length ? blocks.join(', ') : '';
    },
    /** Active `user-roles` as MultiSelect `{ label, value, disabled }` (same shape as UsersManager). */
    activeUserRoleSelectOptions() {
      const enumType = this.getEnumByName(USER_ROLES_ENUM_NAME);
      return (enumType?.values || [])
        .filter((v) => v.isActive)
        .map((v) => ({
          label: v.value,
          value: v.value,
          disabled: false,
        }));
    },
    /** Active roles plus any `accessibleToRoles` entries not in the active enum (shown disabled). */
    roleOptionsForAccessible() {
      const activeVals = this.activeUserRoleSelectOptions;
      const selected = this.currentAssessmentSchema?.accessibleToRoles || [];
      const seen = new Set(activeVals.map((o) => o.value));
      const extra = [];
      for (const r of selected) {
        if (!seen.has(r)) {
          seen.add(r);
          extra.push({
            label: `${r} (inactive)`,
            value: r,
            disabled: true,
          });
        }
      }
      return [...activeVals, ...extra];
    },
    platformClassesOptions() {
      const enumObj = this.getEnumByName('platformClasses');
      // Expecting enumObj.values to be an array of objects.
      // We assume each value is an object with a "value" property.
      return enumObj && enumObj.values ? enumObj.values : [];
    },
    questionTypesOptions() {
      const enumObj = this.getEnumByName('questionTypes');
      return enumObj && enumObj.values ? enumObj.values : [];
    },
    hasUnsavedQuestions() {
      return (
        this.currentAssessmentSchema &&
        this.currentAssessmentSchema.assessmentQuestionArray &&
        this.currentAssessmentSchema.assessmentQuestionArray.some((q) => !q._id)
      );
    },
    hasQuestionsWithoutClass() {
      return (
        this.currentAssessmentSchema &&
        this.currentAssessmentSchema.assessmentQuestionArray &&
        this.currentAssessmentSchema.assessmentQuestionArray.some((q) => q.class === '')
      );
    },
    hasQuestionsWithoutType() {
      return (
        this.currentAssessmentSchema &&
        this.currentAssessmentSchema.assessmentQuestionArray &&
        this.currentAssessmentSchema.assessmentQuestionArray.some((q) => q.type === '')
      );
    },

    // Questions Management Computed Properties
    validQuestionsCount() {
      if (!this.currentAssessmentSchema?.assessmentQuestionArray) return 0;
      return this.currentAssessmentSchema.assessmentQuestionArray.filter(
        (question) => this.validateQuestionDimension(question),
      ).length;
    },

    dimensionFilterOptions() {
      const dimensions = this.currentAssessmentSchema?.dimensions || [];
      if (!Array.isArray(dimensions)) {
        return [{ label: 'All dimensions', value: null }];
      }
      return [
        { label: 'All dimensions', value: null },
        ...dimensions.map((dim) => ({
          label: dim.label || dim.value,
          value: dim.value,
        })),
      ];
    },

    filteredQuestions() {
      if (!this.currentAssessmentSchema?.assessmentQuestionArray) return [];

      let questions = this.currentAssessmentSchema.assessmentQuestionArray;

      if (this.selectedDimensionFilter) {
        questions = questions.filter(
          (question) => question.properties?.dimensionName === this.selectedDimensionFilter,
        );
      }

      return questions;
    },

    filteredQuestionIds() {
      return this.filteredQuestions.map((q) => q._id).filter((id) => id);
    },

    selectAllQuestions: {
      get() {
        const ids = this.filteredQuestionIds;
        return ids.length > 0 && this.selectedQuestions.length === ids.length;
      },
      set(value) {
        this.selectedQuestions = value ? [...this.filteredQuestionIds] : [];
      },
    },

    bulkForceInputMenuItems() {
      return [
        { label: 'Enable', icon: 'pi pi-check', command: () => this.bulkUpdateSelectedQuestions('forceInput', true) },
        { label: 'Disable', icon: 'pi pi-times', command: () => this.bulkUpdateSelectedQuestions('forceInput', false) },
      ];
    },
    bulkPolarityMenuItems() {
      return [
        { label: 'Positive', icon: 'pi pi-thumbs-up', command: () => this.bulkUpdateSelectedQuestions('positive', true) },
        { label: 'Negative', icon: 'pi pi-thumbs-down', command: () => this.bulkUpdateSelectedQuestions('positive', false) },
      ];
    },

    // Dimensions Management Computed Properties
    dimensionsWithQuestionsCount() {
      const dimensions = this.currentAssessmentSchema?.dimensions;
      if (!dimensions || !Array.isArray(dimensions)) return 0;
      return dimensions
        .filter((dimension) => this.hasDimensionQuestions(dimension.value)).length;
    },

    emptyDimensionsCount() {
      const dimensions = this.currentAssessmentSchema?.dimensions;
      if (!dimensions || !Array.isArray(dimensions)) return 0;
      return dimensions
        .filter((dimension) => !this.hasDimensionQuestions(dimension.value)).length;
    },
    expirationTimeInDays() {
      const time = this.currentAssessmentSchema.properties?.expirationTime || 0;
      return (time / (24 * 60 * 60 * 1000)).toFixed(2);
    },
    reminderTimeInDays() {
      const time = this.currentAssessmentSchema.properties?.reminderTime || 0;
      return (time / (24 * 60 * 60 * 1000)).toFixed(2);
    },
    expirationTime() {
      return this.currentAssessmentSchema.properties?.expirationTime || 0;
    },
    reminderTime() {
      return this.currentAssessmentSchema.properties?.reminderTime || 0;
    },
    getForceInputCount() {
      if (!this.currentAssessmentSchema?.assessmentQuestionArray) return 0;
      return this.currentAssessmentSchema.assessmentQuestionArray.filter(
        (q) => q.properties?.options?.forceInput === true,
      ).length;
    },
    linkAfterAssessment() {
      return this.currentAssessmentSchema.properties?.linkAfterAssessment || '/my-results-dashboard';
    },
    /** Display label for "Link After Done" (e.g. "Own Results") instead of raw path when it matches a config option */
    linkAfterAssessmentDisplayLabel() {
      const raw = (this.linkAfterAssessment || '/').trim();
      const base = raw.includes('?') ? raw.split('?')[0].trim().replace(/\/+$/, '') : raw.replace(/\/+$/, '');
      const opts = this.linkAfterAssessmentOptionsFromConfig;
      if (!Array.isArray(opts) || !opts.length) return raw;
      const norm = (v) => (v || '').trim().replace(/\/+$/, '');
      const opt = opts.find((o) => norm(o.value || o) === norm(base));
      if (opt && typeof opt === 'object' && opt.label) return opt.label;
      return raw;
    },

    // DevTips computed properties
    devTipsPeriod() {
      return this.$store.getters['assessmentSchemas/getDevTipsPeriod'](this.currentAssessmentSchema);
    },
    devTipsPeriodLabel() {
      const option = this.devTipsPeriodOptions.find((opt) => opt.value === this.devTipsPeriod);
      return option ? option.label.toLowerCase() : 'weekly';
    },

    // Computed property for DevTips enabled with getter/setter
    devTipsEnabledComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getDevTipsEnabled'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onDevTipsEnabledChange(value);
      },
    },

    // Computed property for DevTips period with getter/setter
    devTipsPeriodComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getDevTipsPeriod'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onDevTipsPeriodChange(value);
      },
    },

    // Computed property for DevTips schedule hour (UTC, for internal use)
    devTipsHourComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getDevTipsHour'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onDevTipsHourChange(value);
      },
    },

    // Computed property for DevTips schedule dayOfWeek
    devTipsDayOfWeekComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getDevTipsDayOfWeek'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onDevTipsDayOfWeekChange(value);
      },
    },

    // Computed property for DevTips schedule dayOfMonth
    devTipsDayOfMonthComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getDevTipsDayOfMonth'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onDevTipsDayOfMonthChange(value);
      },
    },

    // Yearly only (FA-05.24): month of year, 0=January … 11=December
    devTipsMonthComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getDevTipsMonth'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onDevTipsMonthChange(value);
      },
    },

    // Stored schedule displayed as wall-clock (single source of truth)
    devTipsStoredUtcLabel() {
      return formatWallClockSchedule(
        this.devTipsPeriodComputed,
        this.devTipsHourComputed,
        this.devTipsDayOfWeekComputed,
        this.devTipsDayOfMonthComputed,
        this.dayOfWeekOptions,
        this.devTipsMonthComputed,
      );
    },

    // Wall-clock inputs bind directly to stored schedule numbers
    devTipsLocalHourComputed: {
      get() {
        return this.devTipsHourComputed;
      },
      set(value) {
        this.onDevTipsScheduleFromLocal(
          value,
          this.devTipsLocalDayOfWeekComputed,
          this.devTipsLocalDayOfMonthComputed,
          this.devTipsLocalMonthComputed,
        );
      },
    },
    devTipsLocalDayOfWeekComputed: {
      get() {
        return this.devTipsDayOfWeekComputed ?? 1;
      },
      set(value) {
        this.onDevTipsScheduleFromLocal(
          this.devTipsLocalHourComputed,
          value,
          this.devTipsLocalDayOfMonthComputed,
          this.devTipsLocalMonthComputed,
        );
      },
    },
    devTipsLocalDayOfMonthComputed: {
      get() {
        return this.devTipsDayOfMonthComputed ?? 1;
      },
      set(value) {
        this.onDevTipsScheduleFromLocal(
          this.devTipsLocalHourComputed,
          this.devTipsLocalDayOfWeekComputed,
          value,
          this.devTipsLocalMonthComputed,
        );
      },
    },
    devTipsLocalMonthComputed: {
      get() {
        // Wall-clock: stored month is the face value (0=Jan … 11=Dec).
        return this.devTipsMonthComputed ?? 0;
      },
      set(value) {
        this.onDevTipsScheduleFromLocal(
          this.devTipsLocalHourComputed,
          this.devTipsLocalDayOfWeekComputed,
          this.devTipsLocalDayOfMonthComputed,
          value,
        );
      },
    },

    // Computed property for Reflection Guideline enabled with getter/setter
    reflexionGuidelineEnabledComputed: {
      get() {
        const nestedValue = this.currentAssessmentSchema?.config?.reflexionGuideline?.enabled;
        const result = nestedValue || false;
        return result;
      },
      set(value) {
        this.onReflexionGuidelineEnabledChange(value);
      },
    },

    // Computed property for Reflection Guideline initial config
    reflexionGuidelineInitialConfig() {
      const config = this.currentAssessmentSchema?.config?.reflexionGuideline || {};
      const enabled = this.reflexionGuidelineEnabledComputed;

      return {
        ...config,
        enabled,
      };
    },

    // Computed property for Admin Creation enabled with getter/setter
    viewRefGuidelineResultsComputed: {
      get() {
        const config = this.currentAssessmentSchema?.config?.reflexionGuideline || {};
        return config.viewResults || false;
        // return this.$store.getters['assessmentSchemas/getViewRefGuidelineResults'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onViewRefGuidelineResultsChange(value);
      },
    },

    // Check if Question Rotation settings have changes
    hasQuestionRotationChanges() {
      const currentTotal = this.$store.getters['assessmentSchemas/getTotalQuestions'](this.currentAssessmentSchema);
      const currentStickyCount = this.$store.getters['assessmentSchemas/getStickyQuestionsCount'](this.currentAssessmentSchema);
      return this.localTotalQuestions !== currentTotal
        || this.localStickyQuestionsCount !== currentStickyCount;
    },

    // Check if Level System settings have changes
    hasLevelSystemChanges() {
      const currentXp = this.$store.getters['assessmentSchemas/getXpPerCompletion'](this.currentAssessmentSchema);
      return this.localXpPerCompletion !== currentXp;
    },

    // Question rotation computed properties
    questionRotationPeriodLabel() {
      const option = this.rotationPeriodOptions
        .find((opt) => opt.value === this.questionRotationPeriod);
      return option ? option.label.toLowerCase() : 'weekly';
    },

    // Computed property for question rotation enabled with getter/setter
    questionRotationEnabledComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getQuestionRotationEnabled'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onQuestionRotationEnabledChange(value);
      },
    },

    // Computed property for question rotation period with getter/setter
    questionRotationPeriodComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getQuestionRotationPeriod'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onQuestionRotationPeriodChange(value);
      },
    },

    // Computed property for question rotation schedule hour
    questionRotationHourComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getQuestionRotationHour'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onQuestionRotationHourChange(value);
      },
    },

    // Computed property for question rotation schedule dayOfWeek
    questionRotationDayOfWeekComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getQuestionRotationDayOfWeek'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onQuestionRotationDayOfWeekChange(value);
      },
    },

    // Computed property for question rotation schedule dayOfMonth
    questionRotationDayOfMonthComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getQuestionRotationDayOfMonth'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onQuestionRotationDayOfMonthChange(value);
      },
    },

    questionRotationStoredUtcLabel() {
      return formatWallClockSchedule(
        this.questionRotationPeriodComputed,
        this.questionRotationHourComputed,
        this.questionRotationDayOfWeekComputed,
        this.questionRotationDayOfMonthComputed,
        this.dayOfWeekOptions,
      );
    },

    questionRotationLocalHourComputed: {
      get() {
        return this.questionRotationHourComputed;
      },
      set(value) {
        this.onQuestionRotationScheduleFromLocal(
          value,
          this.questionRotationLocalDayOfWeekComputed,
          this.questionRotationLocalDayOfMonthComputed,
        );
      },
    },
    questionRotationLocalDayOfWeekComputed: {
      get() {
        return this.questionRotationDayOfWeekComputed ?? 1;
      },
      set(value) {
        this.onQuestionRotationScheduleFromLocal(
          this.questionRotationLocalHourComputed,
          value,
          this.questionRotationLocalDayOfMonthComputed,
        );
      },
    },
    questionRotationLocalDayOfMonthComputed: {
      get() {
        return this.questionRotationDayOfMonthComputed ?? 1;
      },
      set(value) {
        this.onQuestionRotationScheduleFromLocal(
          this.questionRotationLocalHourComputed,
          this.questionRotationLocalDayOfWeekComputed,
          value,
        );
      },
    },

    // Computed property for question source with getter/setter
    questionSourceComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getQuestionSource'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onQuestionSourceChange(value);
      },
    },

    // Computed property for total questions with getter/setter
    totalQuestionsComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getTotalQuestions'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onTotalQuestionsChange(value);
      },
    },

    stickyQuestionsEnabledComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getStickyQuestionsEnabled'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onStickyQuestionsEnabledChange(value);
      },
    },

    stickyQuestionsCountComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getStickyQuestionsCount'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onStickyQuestionsCountChange(value);
      },
    },

    stickyQuestionSourceComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getStickyQuestionSource'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onStickyQuestionSourceChange(value);
      },
    },

    availableStickyQuestionsCount() {
      const source = this.stickyQuestionSourceComputed;
      if (source === 'dimensions') {
        const dims = this.currentAssessmentSchema?.dimensions || [];
        let count = 0;
        dims.forEach((dim) => {
          (dim.questions || []).forEach((q) => {
            if (q.sticky) count += 1;
          });
        });
        return count;
      }
      const questions = this.currentAssessmentSchema?.assessmentQuestionArray || [];
      return questions.filter((q) => q.sticky).length;
    },

    // Computed property for Level System enabled with getter/setter
    levelSystemEnabledComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getLevelSystemEnabled'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onLevelSystemEnabledChange(value);
      },
    },

    // Computed property for Admin Creation enabled with getter/setter
    canBeCreatedByAdminComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getCanBeCreatedByAdmin'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onCanBeCreatedByAdminChange(value);
      },
    },

    // Computed property for XP per completion with getter/setter
    xpPerCompletionComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getXpPerCompletion'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onXpPerCompletionChange(value);
      },
    },

    // Computed property for First Login Task enabled with getter/setter
    isFirstLoginTaskComputed: {
      get() {
        return this.$store.getters['assessmentSchemas/getFirstLoginTaskEnabled'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onIsFirstLoginTaskChange(value);
      },
    },

    // Computed property for canCreateExternalEvaluation with getter/setter
    canCreateExternalEvaluationComputed: {
      get() {
        // Use local state if set, otherwise use schema value from config
        if (this.localCanCreateExternalEvaluation !== null) {
          return this.localCanCreateExternalEvaluation;
        }
        return this.$store.getters['assessmentSchemas/getCanCreateExternalEvaluation'](this.currentAssessmentSchema);
      },
      set(value) {
        this.onCanCreateExternalEvaluationChange(value);
      },
    },

    // Display values for external evaluation (use getters so config is respected)
    externalEvaluationCanCreateForDisplay() {
      return this.$store.getters['assessmentSchemas/getCanCreateExternalEvaluation'](this.currentAssessmentSchema);
    },
    externalEvaluationSchemaIdForDisplay() {
      return this.$store.getters['assessmentSchemas/getExternalEvaluationSchemaId'](this.currentAssessmentSchema) || null;
    },

    // Generated external schema type based on parent schema type
    generatedExternalSchemaType() {
      return `external_${this.currentAssessmentSchema?.type || 'evaluation'}`;
    },

    // Computed property for canDisplayExternalEmails with getter/setter
    canDisplayExternalEmailsComputed: {
      get() {
        return this.currentAssessmentSchema?.canDisplayExternalEmails || false;
      },
      set(value) {
        this.onCanDisplayExternalEmailsChange(value);
      },
    },

    // Computed property to check if participant limits have changed and are valid
    hasParticipantLimitsChanged() {
      // Check if values are empty or null
      const minIsEmpty = this.localMinParticipants === null || this.localMinParticipants === undefined || this.localMinParticipants === '';
      const maxIsEmpty = this.localMaxParticipants === null || this.localMaxParticipants === undefined || this.localMaxParticipants === '';

      // If either value is empty, button should be disabled
      if (minIsEmpty || maxIsEmpty) {
        return false;
      }

      const schemaMin = this.currentAssessmentSchema?.minParticipants || 7;
      const schemaMax = this.currentAssessmentSchema?.maxParticipants || 10;
      return this.localMinParticipants !== schemaMin || this.localMaxParticipants !== schemaMax;
    },

    hasExtensionDaysChanged() {
      const isEmpty =
        this.localExtensionDays === null
        || this.localExtensionDays === undefined
        || this.localExtensionDays === '';
      if (isEmpty) {
        return false;
      }
      const schemaValue = this.currentAssessmentSchema?.extensionDays ?? 14;
      return this.localExtensionDays !== schemaValue;
    },

    hasRecreationIntervalSettingsChanged() {
      return hasRecreationIntervalSettingsChangedHelper(
        this.localRecreationImmediateAfterCycle === true,
        this.localRecreationIntervalDays,
        this.currentAssessmentSchema?.recreationImmediateAfterCycle === true,
        this.currentAssessmentSchema?.recreationIntervalDays,
      );
    },

    // Computed property to check if minAssessmentsForCharts has changed
    hasMinAssessmentsForChartsChanged() {
      const isEmpty = this.localMinAssessmentsForCharts === null || this.localMinAssessmentsForCharts === undefined || this.localMinAssessmentsForCharts === '';
      if (isEmpty) {
        return false;
      }
      const schemaValue = this.currentAssessmentSchema?.minAssessmentsForCharts || 5;
      return this.localMinAssessmentsForCharts !== schemaValue;
    },

    // Computed property to check if dimensions are read-only
    // Dimensions are now always configurable, including for external evaluation schemas
    isDimensionsReadOnly() {
      return false;
    },

    canDisplayIntroPageComputed: {
      get() {
        return this.currentAssessmentSchema?.config?.intro?.enabled || false;
      },
      set(value) {
        this.onCanDisplayExternalEmailsChange(value);
      },
    },

    introEnabledComputed: {
      get() {
        return this.localIntroConfig?.enabled ?? false;
      },
      set(value) {
        this.localIntroConfig.enabled = value;
        this.onCanDisplayIntroPageChange(value);
      },
    },

    /** Aggregate hidden Unicode / &nbsp; detection across intro rich-text editors (current language). */
    introRichTextHiddenCharsAggregate() {
      if (!this.localIntroConfig?.enabled) {
        return { found: false, count: 0, types: [] };
      }
      const lang = this.introLang;
      const fields = [
        this.localIntroConfig.content?.[lang],
        this.localIntroConfig.instructionsContent?.[lang],
        this.localIntroConfig.additionalInfoContent?.[lang],
      ];
      let totalCount = 0;
      const typeSet = new Set();
      fields.forEach((html) => {
        const info = detectHiddenChars(html || '');
        totalCount += info.count;
        info.types.forEach((t) => typeSet.add(t));
      });
      return { found: totalCount > 0, count: totalCount, types: [...typeSet] };
    },
    introIntroRichTextFieldsHaveContent() {
      const lang = this.introLang;
      const strip = (h) => String(h || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
      return !!(
        strip(this.localIntroConfig.content?.[lang])
        || strip(this.localIntroConfig.instructionsContent?.[lang])
        || strip(this.localIntroConfig.additionalInfoContent?.[lang])
      );
    },
    introShowHiddenCharsOkBanner() {
      return this.localIntroConfig?.enabled
        && !this.introRichTextHiddenCharsAggregate.found
        && this.introIntroRichTextFieldsHaveContent;
    },
    introPreviewMainContentHtml() {
      const raw = this.localIntroConfig?.content?.[this.introLang] || '';
      const textOnly = raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/\s/g, '');
      if (!textOnly) return 'Introduction content';
      const info = detectHiddenChars(raw);
      return info.found ? highlightHiddenChars(raw) : raw;
    },
    introPreviewInstructionsHtml() {
      const raw = this.localIntroConfig?.instructionsContent?.[this.introLang] || '';
      const info = detectHiddenChars(raw);
      return info.found ? highlightHiddenChars(raw) : raw;
    },
    introPreviewAdditionalHtml() {
      const raw = this.localIntroConfig?.additionalInfoContent?.[this.introLang] || '';
      const info = detectHiddenChars(raw);
      return info.found ? highlightHiddenChars(raw) : raw;
    },

  },
  async created() {
    const schemaId = this.$route.params.id;
    // Load all schemas first (needed for reflection guideline mapping)
    await this.$store.dispatch('assessmentSchemas/fetchAssessments');
    // Then load the specific schema
    await this.$store.dispatch('assessmentSchemas/fetchAssessment', schemaId);
    this.checkRequiredEnums();
    this.getAssessmentName();
  },
  beforeRouteLeave(to, from, next) {
    // Clear the currentTask when leaving the edit view.
    this.$store.commit('assessmentSchemas/CLEAR_CURRENT_ASSESSMENT');
    next();
  },
  watch: {
    // Watch for changes in the schema to update the name.
    currentAssessmentSchema: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.getAssessmentName();
          this.initLocalValues();
          this.loadReflexionGuidelineConfig();
          // Schema templates are now managed at tenant level, not schema level
          // this.loadSchemaTemplates();
          // Initialize external evaluation settings (use getters so config is respected)
          const schemaId = this.$store.getters['assessmentSchemas/getExternalEvaluationSchemaId'](newVal);
          this.selectedExternalEvaluationSchemaId = schemaId || null;
          this.previousExternalEvaluationSchemaId = schemaId || null;
          // Reset local checkbox state to sync with schema
          this.localCanCreateExternalEvaluation = null;
          this.loadExternalEvaluationSchemaInfo();
          this.loadAvailableSchemasForExternalEvaluation();
          this.syncLocalIntroFromSchema();
        }
      },
    },
  },
  mounted() {
    this.fetchEnumTypes();
  },
  methods: {
    ...mapActions('enums', ['fetchEnumTypes']),

    /**
     * Returns which config blocks need migration. Need migration when the root
     * has the field(s) but config does NOT contain that block. If the block
     * exists in config, config is the source of truth.
     *
     * The API can return Mongoose-initialised default objects (e.g.
     * config.externalEvaluation: { canCreate: false, schemaId: null }) so we
     * treat a block as "present" only when it has real data — otherwise we
     * consider it missing and require migration.
     */
    getUnmigratedBlocks(schema) {
      if (!schema || typeof schema !== 'object') return [];
      const c = schema.config && typeof schema.config === 'object'
        ? schema.config : {};
      const out = [];

      if (!c.devTips
        && (schema.devTipsEnabled !== undefined || schema.devTipsPeriod !== undefined)) {
        out.push('devTips');
      }
      if (!c.questionRotation
        && (schema.rotatedQuestionsSchema !== undefined
          || schema.rotationPeriod !== undefined
          || schema.questionSource !== undefined
          || schema.totalQuestions !== undefined)) {
        out.push('questionRotation');
      }
      if (!c.levelSystem
        && (schema.levelSystemEnabled !== undefined
          || schema.xpPerCompletion !== undefined)) {
        out.push('levelSystem');
      }
      if (!c.firstLoginTask && schema.isFirstLoginTask !== undefined) {
        out.push('firstLoginTask');
      }

      // Root may have Mongoose defaults (canCreate: false, canBeCreatedByAdmin: false).
      // Only flag when root has a meaningful value to migrate (true or schemaId set).
      const hasExternalEval = c.externalEvaluation
        && (c.externalEvaluation.canCreate === true || c.externalEvaluation.schemaId != null);
      const rootWantsExternalEval = schema.canCreateExternalEvaluation === true
        || (schema.externalEvaluationSchemaId != null && schema.externalEvaluationSchemaId !== '');
      if (!hasExternalEval && rootWantsExternalEval) {
        out.push('externalEvaluation');
      }

      const hasAdminCreation = c.adminCreation
        && c.adminCreation.canBeCreatedByAdmin === true;
      if (!hasAdminCreation && schema.canBeCreatedByAdmin === true) {
        out.push('adminCreation');
      }
      return out;
    },

    schemaNeedsMigration(schema) {
      return this.getUnmigratedBlocks(schema).length > 0;
    },

    checkRequiredEnums() {
      // Check for required enums: platformClasses and questionTypes.
      const platformClasses = this.getEnumByName('platformClasses');
      const questionTypes = this.getEnumByName('questionTypes');
      if (!platformClasses || !platformClasses.values || platformClasses.values.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Missing Enum',
          detail: 'Platform Classes enum is missing. Please create it first.',
          life: 3000,
        });
      }
      if (!questionTypes || !questionTypes.values || questionTypes.values.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Missing Enum',
          detail: 'Question Types enum is missing. Please create it first.',
          life: 3000,
        });
      }
    },
    getAssessmentName() {
      this.currentSchemaName = {
        en: this.currentAssessmentSchema.representationProperties?.assessmentName?.en ?? '',
        de: this.currentAssessmentSchema.representationProperties?.assessmentName?.de ?? '',
      };
    },

    initLocalValues() {
      if (this.currentAssessmentSchema) {
        this.localExpirationTime = this.currentAssessmentSchema.properties?.expirationTime || 0;
        this.localReminderTime = this.currentAssessmentSchema.properties?.reminderTime || 0;
        this.localLinkAfterAssessment =
          this.currentAssessmentSchema.properties?.linkAfterAssessment || '/my-results-dashboard';

        // Initialize local values for input fields using helper functions
        this.localTotalQuestions = this.$store.getters['assessmentSchemas/getTotalQuestions'](this.currentAssessmentSchema);
        this.localStickyQuestionsCount = this.$store.getters['assessmentSchemas/getStickyQuestionsCount'](this.currentAssessmentSchema);
        this.localXpPerCompletion = this.$store.getters['assessmentSchemas/getXpPerCompletion'](this.currentAssessmentSchema);

        // Initialize schedule settings
        this.devTipsSchedule = {
          hour: this.$store.getters['assessmentSchemas/getDevTipsHour'](this.currentAssessmentSchema),
          dayOfWeek: this.$store.getters['assessmentSchemas/getDevTipsDayOfWeek'](this.currentAssessmentSchema),
          dayOfMonth: this.$store.getters['assessmentSchemas/getDevTipsDayOfMonth'](this.currentAssessmentSchema),
          month: this.$store.getters['assessmentSchemas/getDevTipsMonth'](this.currentAssessmentSchema),
        };
        this.questionRotationSchedule = {
          hour: this.$store.getters['assessmentSchemas/getQuestionRotationHour'](this.currentAssessmentSchema),
          dayOfWeek: this.$store.getters['assessmentSchemas/getQuestionRotationDayOfWeek'](this.currentAssessmentSchema),
          dayOfMonth: this.$store.getters['assessmentSchemas/getQuestionRotationDayOfMonth'](this.currentAssessmentSchema),
        };

        // Initialize participant limits
        this.localMinParticipants = this.currentAssessmentSchema.minParticipants || 7;
        this.localMaxParticipants = this.currentAssessmentSchema.maxParticipants || 10;
        this.localExtensionDays = this.currentAssessmentSchema.extensionDays ?? 14;
        this.localRecreationImmediateAfterCycle =
          this.currentAssessmentSchema.recreationImmediateAfterCycle === true;
        this.localRecreationIntervalDays =
          this.currentAssessmentSchema.recreationIntervalDays ?? 0;

        // Initialize chart display configuration
        this.localMinAssessmentsForCharts =
          this.currentAssessmentSchema.minAssessmentsForCharts || 5;

        // DevTips settings are now handled by computed properties from store
      }
    },
    async saveAssessmentSchemaName() {
      const payload = {
        representationProperties: {
          assessmentName: {
            en: this.currentSchemaName.en,
            de: this.currentSchemaName.de,
          },
        },
      };
      await this.$store.dispatch('assessmentSchemas/updateAssessment', {
        id: this.currentAssessmentSchema._id,
        payload,
      });
    },
    async updateQuestion(index, updatedQuestion) {
      // Update the question at index.
      // this.currentAssessmentSchema.assessmentQuestionArray.splice(index, 1, updatedQuestion);
      // will update the question
      console.log('Updated Question:', updatedQuestion);
      await this.$store.dispatch('assessmentSchemas/updateQuestion', {
        assessmentId: this.currentAssessmentSchema._id,
        questionId: updatedQuestion._id,
        questionData: updatedQuestion,
      });
    },
    async saveQuestion(question) {
      // Save the question.
      console.log('Saving Question:', question);
      await this.$store.dispatch('assessmentSchemas/addQuestion', {
        assessmentId: this.currentAssessmentSchema._id,
        questionData: question,
      });
    },

    async confirmDeleteQuestion() {
      if (!this.questionToDelete) return;

      this.isDeletingQuestion = true;
      try {
        const { question } = this.questionToDelete;

        // Call the store action to delete from backend
        // The store mutation will automatically remove the question from the local array
        await this.$store.dispatch('assessmentSchemas/deleteQuestion', {
          assessmentId: this.currentAssessmentSchema._id,
          questionId: question._id,
        });
      } catch (error) {
        console.error('Failed to delete question:', error);
        // Error notification is handled by the store
      } finally {
        this.isDeletingQuestion = false;
        this.showDeleteConfirmation = false;
        this.questionToDelete = null;
      }
    },

    cancelDeleteQuestion() {
      this.showDeleteConfirmation = false;
      this.questionToDelete = null;
      // No notification needed for user cancellation
    },

    // Questions Management Methods
    onDimensionFilterChange() {
      // Reset selection when filter changes
      this.selectedQuestions = [];
    },

    toggleQuestionSelection(question) {
      if (!question._id) return;
      const idx = this.selectedQuestions.indexOf(question._id);
      if (idx === -1) {
        this.selectedQuestions = [...this.selectedQuestions, question._id];
      } else {
        this.selectedQuestions = this.selectedQuestions.filter((id) => id !== question._id);
      }
    },

    async bulkUpdateSelectedQuestions(field, value) {
      if (this.selectedQuestions.length === 0) return;
      const assessmentId = this.currentAssessmentSchema._id;
      const questions = this.currentAssessmentSchema.assessmentQuestionArray || [];
      const questionsData = this.selectedQuestions.map((id) => {
        const question = questions.find((q) => q._id === id);
        if (!question) return { id, data: {} };
        const patch = field === 'forceInput'
          ? {
            properties: {
              ...question.properties,
              options: {
                ...question.properties?.options,
                forceInput: value,
              },
            },
          }
          : {
            properties: {
              ...question.properties,
              data: { ...question.properties?.data, positive: value },
            },
          };
        return { id, data: { ...question, ...patch } };
      }).filter((item) => Object.keys(item.data).length > 0);
      if (questionsData.length === 0) return;
      try {
        await this.$store.dispatch('assessmentSchemas/updateQuestions', {
          assessmentId,
          questionsData,
          silent: true,
        });
        await this.refreshAssessmentSchema();
        this.$toast.add({
          severity: 'success',
          summary: 'Bulk Update',
          detail: `Updated ${questionsData.length} question(s)`,
          life: 3000,
        });
      } catch (error) {
        console.error('Bulk update failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update selected questions',
          life: 3000,
        });
      }
    },

    confirmBulkDelete() {
      this.$confirm.require({
        message: `Are you sure you want to delete ${this.selectedQuestions.length} selected questions?`,
        header: 'Confirm Bulk Delete',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.bulkDeleteQuestions();
        },
        reject: () => {
          // User cancelled
        },
      });
    },

    async bulkDeleteQuestions() {
      try {
        // Delete all selected questions in parallel
        const deletePromises = this.selectedQuestions
          .map((questionId) => this.$store.dispatch('assessmentSchemas/deleteQuestion', {
            assessmentId: this.currentAssessmentSchema._id,
            questionId,
            silent: true,
          }));

        await Promise.all(deletePromises);

        // Clear selection
        this.selectedQuestions = [];

        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Selected questions deleted successfully',
          life: 3000,
        });
      } catch (error) {
        console.error('Failed to delete questions:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete some questions',
          life: 3000,
        });
      }
    },

    exportSelectedQuestions() {
      const questionsToExport = this.filteredQuestions
        .filter((q) => this.selectedQuestions.includes(q._id));

      const dataStr = JSON.stringify(questionsToExport, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `questions-export-${new Date().toISOString().split('T')[0]}.json`;
      link.click();

      URL.revokeObjectURL(url);

      this.$toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Questions exported successfully',
        life: 3000,
      });
    },

    createTestAssessment() {
      // Create a test assessment for development purposes
      this.$confirm.require({
        message: 'Create a test assessment for development purposes?',
        header: 'Create Test Assessment',
        icon: 'pi pi-user',
        accept: () => {
          this.$toast.add({
            severity: 'info',
            summary: 'Test Assessment',
            detail: 'Test assessment creation feature coming soon',
            life: 3000,
          });
        },
        reject: () => {
          // User cancelled
        },
      });
    },

    // Question expansion management
    toggleQuestionExpanded(index) {
      this.expandedQuestionIndex = this.expandedQuestionIndex === index ? -1 : index;
    },

    collapseQuestion() {
      this.expandedQuestionIndex = -1;
    },

    // Question validation
    getQuestionValidationErrors(question) {
      return validateQuestion(question);
    },

    // Smart delete handler with confirmation
    handleQuestionDelete(index) {
      const question = this.currentAssessmentSchema.assessmentQuestionArray[index];

      // Check if question has an ID (exists in database)
      if (!question._id) {
        // Question is not saved yet, just remove from local array
        this.currentAssessmentSchema.assessmentQuestionArray.splice(index, 1);
        return;
      }

      // Store the question to delete and show confirmation dialog
      this.questionToDelete = { question, index };
      this.showDeleteConfirmation = true;
    },
    openNewQuestionDialog() {
      this.newQuestionDialogVisible = true;
      // Reset newQuestion fields if needed.
      this.newQuestion = {
        properties: {
          dimensionName: '',
          dimension: '',
          content: {
            text_questionProgress: { en: '', de: '' },
            text_question: { en: '', de: '' },
          },
          options: { forceInput: false },
          data: {},
        },
        class: '',
        type: '',
        viewTimeLogs: [],
      };
    },
    closeNewQuestionDialog() {
      console.log('Closing new question dialog');
      this.newQuestionDialogVisible = false;
    },
    async addNewQuestion(questionData) {
      try {
        await this.$store.dispatch('assessmentSchemas/addQuestion', {
          assessmentId: this.currentAssessmentSchema._id,
          questionData,
        });
        this.closeNewQuestionDialog();
        await this.refreshAssessmentSchema();
        // Store already shows FA-02.39 success/error toasts
      } catch (error) {
        console.error('Failed to add question:', error);
      }
    },
    openExcelImportDialog() {
      this.errorLogs = [];
      this.excelImportDialogVisible = true;
    },
    closeExcelImportDialog() {
      this.excelImportDialogVisible = false;
    },
    openSmartExcelImportDialog() {
      this.smartExcelImportDialogVisible = true;
    },
    closeSmartExcelImportDialog() {
      this.smartExcelImportDialogVisible = false;
    },
    async onExcelFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        const questions = await parseExcelFile(file);
        const processedQuestions = processImportedQuestions(questions);

        if (!processedQuestions.length) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Import',
            detail: 'No valid questions found in the Excel file.',
            life: 3000,
          });
          return;
        }

        await this.$store.dispatch('assessmentSchemas/addQuestions', {
          assessmentId: this.currentAssessmentSchema._id,
          questionsData: processedQuestions,
        });
        await this.refreshAssessmentSchema();
        // Store action shows the FA-02.39 success toast after persist
      } catch (error) {
        console.log('Excel import error: ', error.message);
        this.errorLogs.push('Excel import error: ', error.message);
        // Store already toasts on API failure; only toast for parse/local failures
        if (!error?.response) {
          this.$toast.add({
            severity: 'error',
            summary: 'Import Error',
            detail: error.message || 'Failed to import questions from Excel.',
            life: 3000,
          });
        }
      } finally {
        this.closeExcelImportDialog();
      }
    },

    /**
     * Smart Excel import that updates existing questions and adds new ones
     */
    async onSmartExcelFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        // Parse file to questions array
        const questions = await parseExcelFile(file);
        const processedQuestions = processImportedQuestions(questions);

        let updatedCount = 0;
        let addedCount = 0;
        const questionsToSave = [];

        // Process each question from Excel
        processedQuestions.forEach((excelQuestion) => {
          // Try to find existing question by matching text content
          const existingQuestionIndex = this.currentAssessmentSchema
            .assessmentQuestionArray.findIndex((existing) => {
              // Debug logging to see what we're comparing
              console.log('🔍 Comparing questions:');
              console.log('Excel question:', {
                en: excelQuestion.properties.content.text_question.en,
                de: excelQuestion.properties.content.text_question.de,
                dimension: excelQuestion.properties.dimensionName,
              });
              console.log('Existing question:', {
                en: existing.properties.content.text_question.en,
                de: existing.properties.content.text_question.de,
                dimension: existing.properties.dimensionName,
              });

              // More flexible matching - check if text contains the same content
              const enMatch = existing.properties.content.text_question.en
               === excelQuestion.properties.content.text_question.en;
              const deMatch = existing.properties.content.text_question.de
               === excelQuestion.properties.content.text_question.de;
              const dimensionMatch = existing.properties.dimensionName
               === excelQuestion.properties.dimensionName;

              // Alternative: more flexible text matching (normalized comparison)
              const normalizedEnMatch = this.normalizeText(
                existing.properties.content.text_question.en,
              ) === this.normalizeText(
                excelQuestion.properties.content.text_question.en,
              );
              const normalizedDeMatch = this.normalizeText(
                existing.properties.content.text_question.de,
              ) === this.normalizeText(
                excelQuestion.properties.content.text_question.de,
              );

              console.log('Matches:', {
                enMatch,
                deMatch,
                dimensionMatch,
                normalizedEnMatch,
                normalizedDeMatch,
              });

              // Return true if we have a match on any of the text fields (exact or normalized)
              return (
                enMatch ||
                deMatch ||
                normalizedEnMatch ||
                normalizedDeMatch
              ) && dimensionMatch;
            });

          console.log(`Found existing question at index: ${existingQuestionIndex}`);

          if (existingQuestionIndex !== -1) {
            // Replace existing question entirely with new Excel data
            const existingQuestion = this.currentAssessmentSchema
              .assessmentQuestionArray[existingQuestionIndex];

            // Preserve the existing ID but replace all other data
            const updatedQuestion = {
              ...excelQuestion,
              _id: existingQuestion._id, // Keep the existing ID
            };

            // Replace the question in the array
            this.currentAssessmentSchema.assessmentQuestionArray
              .splice(existingQuestionIndex, 1, updatedQuestion);

            // Mark for update if it has an ID
            if (existingQuestion._id) {
              questionsToSave.push({
                action: 'update',
                questionId: existingQuestion._id,
                questionData: updatedQuestion,
              });
            }
            updatedCount += 1;
            console.log(`✅ Updated existing question: ${existingQuestion._id}`);
          } else {
            // Add as new question
            this.currentAssessmentSchema.assessmentQuestionArray.push(excelQuestion);
            questionsToSave.push({
              action: 'add',
              questionData: excelQuestion,
            });
            addedCount += 1;
            console.log(`➕ Added new question: ${excelQuestion.properties.content.text_question.en}`);
          }
        });

        // Bulk save all changes (silent store toasts → one FA-02.39 summary)
        if (questionsToSave.length > 0) {
          await this.bulkSaveQuestions(questionsToSave);
        }

        this.$toast.add({
          severity: 'success',
          summary: 'Smart Import Successful',
          detail: `Updated ${updatedCount} existing questions, added ${addedCount} new questions.`,
          life: 5000,
        });
      } catch (error) {
        console.error('Smart Excel import error:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Smart Import Error',
          detail: error.message || 'Failed to perform smart import from Excel.',
          life: 5000,
        });
      } finally {
        this.closeSmartExcelImportDialog();
      }
    },

    /**
     * Bulk save questions (updates and additions)
     */
    async bulkSaveQuestions(questionsToSave) {
      try {
        const updates = questionsToSave.filter((q) => q.action === 'update');
        const additions = questionsToSave.filter((q) => q.action === 'add');

        // Process updates (silent — caller shows one summary toast)
        const updatePromises = updates.map((update) => this.$store
          .dispatch('assessmentSchemas/updateQuestion', {
            assessmentId: this.currentAssessmentSchema._id,
            questionId: update.questionId,
            questionData: update.questionData,
            silent: true,
          }));
        await Promise.all(updatePromises);

        // Process additions
        if (additions.length > 0) {
          const newQuestions = additions.map((q) => q.questionData);
          await this.$store.dispatch('assessmentSchemas/addQuestions', {
            assessmentId: this.currentAssessmentSchema._id,
            questionsData: newQuestions,
            silent: true,
          });
        }

        // Refresh the schema to get updated data
        await this.refreshAssessmentSchema();
      } catch (error) {
        console.error('Bulk save failed:', error);
        throw new Error(`Failed to save questions: ${error.message}`);
      }
    },

    // Helper to normalize text for comparison
    normalizeText(text) {
      if (!text) return '';
      return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Remove accents and diacritics
    },

    // Bulk toggle forceInput flag for all questions
    async bulkToggleForceInput(forceInputValue) {
      if (!this.currentAssessmentSchema?.assessmentQuestionArray?.length) {
        this.$toast.add({
          severity: 'warn',
          summary: 'No Questions',
          detail: 'No questions available to update.',
          life: 3000,
        });
        return;
      }

      try {
        // Update all questions in the local array
        this.currentAssessmentSchema.assessmentQuestionArray =
          this.currentAssessmentSchema.assessmentQuestionArray.map(
            (question) => {
              // Create a new object to avoid mutating the parameter
              const updatedQuestion = { ...question };

              // Ensure the options object exists
              if (!updatedQuestion.properties) {
                updatedQuestion.properties = {};
              }
              if (!updatedQuestion.properties.options) {
                updatedQuestion.properties.options = {};
              }

              // Update the forceInput flag
              updatedQuestion.properties.options.forceInput = forceInputValue;

              return updatedQuestion;
            },
          );

        // Persist first, then confirm (FA-02.39)
        await this.saveAllQuestions({ silent: true });

        const action = forceInputValue ? 'enabled' : 'disabled';
        this.$toast.add({
          severity: 'success',
          summary: 'Bulk Update Successful',
          detail: `Force input ${action} for all ${this.currentAssessmentSchema.assessmentQuestionArray.length} questions.`,
          life: 3000,
        });
      } catch (error) {
        console.error('Bulk toggle forceInput failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Bulk Update Failed',
          detail: `Failed to update force input: ${error.message}`,
          life: 5000,
        });
      }
    },

    // Save all questions to backend
    async saveAllQuestions({ silent = false } = {}) {
      try {
        const questionsToSave = this.currentAssessmentSchema.assessmentQuestionArray.filter(
          (q) => q._id, // Only save questions that have been saved before
        );

        if (questionsToSave.length === 0) {
          console.log('No questions to save');
          return;
        }

        // Update all questions in parallel
        await Promise.all(
          questionsToSave.map((question) => this.$store
            .dispatch('assessmentSchemas/updateQuestion', {
              assessmentId: this.currentAssessmentSchema._id,
              questionId: question._id,
              questionData: question,
              silent,
            })),
        );

        // Refresh the schema to get updated data
        await this.refreshAssessmentSchema();
      } catch (error) {
        console.error('Save all questions failed:', error);
        throw new Error(`Failed to save questions: ${error.message}`);
      }
    },
    openJsonImportDialog() {
      this.errorLogs = [];
      this.jsonImportText = '';
      this.jsonImportDialogVisible = true;
    },
    closeJsonImportDialog() {
      this.jsonImportDialogVisible = false;
    },
    async importQuestionsFromJson() {
      try {
        const imported = JSON.parse(this.jsonImportText);
        if (!Array.isArray(imported)) {
          throw new Error('JSON must be an array of questions.');
        }
        if (!imported.length) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Import',
            detail: 'No questions found in the JSON.',
            life: 3000,
          });
          return;
        }

        await this.$store.dispatch('assessmentSchemas/addQuestions', {
          assessmentId: this.currentAssessmentSchema._id,
          questionsData: imported,
        });
        await this.refreshAssessmentSchema();
        // Store action shows the FA-02.39 success toast after persist
      } catch (error) {
        this.errorLogs.push('JSON import error: ', error.message);
        if (!error?.response) {
          this.$toast.add({
            severity: 'error',
            summary: 'Import Error',
            detail: error.message || 'Failed to import questions from JSON.',
            life: 3000,
          });
        }
      } finally {
        this.closeJsonImportDialog();
      }
    },
    async saveAllUnsavedQuestions() {
      // Get unsaved questions (those missing _id)
      try {
        const unsavedQuestions = this.currentAssessmentSchema.assessmentQuestionArray.filter(
          (q) => !q._id,
        );
        if (!unsavedQuestions.length) {
          return;
        }
        await this.$store.dispatch('assessmentSchemas/addQuestions', {
          assessmentId: this.currentAssessmentSchema._id,
          questionsData: unsavedQuestions,
        });
        await this.refreshAssessmentSchema();
      } catch (error) {
        this.errorLogs.push(error?.message || error);
        throw error;
      }
    },
    openApplyClassAndTypeDialog() {
      this.currentAssessmentSchema.assessmentQuestionArray =
        this.currentAssessmentSchema.assessmentQuestionArray.map((q) => ({
          ...q,
          class: this.globalSelectedQuestionsClass,
          type: this.globalSelectedQuestionsType,
        }));
    },
    async openTimeSettingsOverlay() {
      await this.$store.dispatch('config/fetchConfig').catch(() => {});
      this.timeSettingsOverlayVisible = true;
      this.localExpirationTime = this.expirationTime;
      this.localReminderTime = this.reminderTime;
      this.localLinkAfterAssessment = this.linkAfterAssessment;
    },
    async saveNewTimeSettings() {
      const payload = {
        properties: {
          expirationTime: this.localExpirationTime,
          reminderTime: this.localReminderTime,
          linkAfterAssessment: this.localLinkAfterAssessment,
        },
      };
      await this.$store.dispatch('assessmentSchemas/updateAssessment', {
        id: this.currentAssessmentSchema._id,
        payload,
      });
      await this.$store.dispatch('assessmentSchemas/fetchAssessment', this.currentAssessmentSchema._id).catch(() => {});
      const fullPath = (this.localLinkAfterAssessment || '').trim() || '/';
      const basePath = fullPath.includes('?') ? fullPath.split('?')[0] : fullPath;
      const options = this.linkAfterAssessmentOptionsFromConfig;
      const exists = options.some((o) => (o.value || o) === basePath || (o.value || o) === fullPath);
      if (!exists && basePath) {
        const updated = [...options.map((o) => (typeof o === 'string' ? { label: o, value: o } : { ...o })), { label: basePath, value: basePath }];
        await this.$store.dispatch('config/updateConfig', { linkAfterAssessmentOptions: updated }).catch(() => {});
        await this.$store.dispatch('config/fetchConfig').catch(() => {});
      }
      this.closeTimeSettingsOverlay();
    },
    closeTimeSettingsOverlay() {
      this.timeSettingsOverlayVisible = false;
    },

    async onRolesChange() {
      const payload = {
        accessibleToRoles: this.currentAssessmentSchema.accessibleToRoles,
      };
      await this.$store.dispatch('assessmentSchemas/updateAssessment', {
        id: this.currentAssessmentSchema._id,
        payload,
      });
    },

    // Add method to refresh the assessment schema
    async refreshAssessmentSchema() {
      await this.$store.dispatch(
        'assessmentSchemas/fetchAssessment',
        this.currentAssessmentSchema._id,
      );
    },

    // Reflection Guideline methods
    async onReflexionGuidelineEnabledChange(newValue) {
      // Store the previous value in case we need to revert
      const previousEnabled = this.currentAssessmentSchema?.config?.reflexionGuideline?.enabled
       || false;

      // Optimistically update the nested config
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.reflexionGuideline) {
        this.currentAssessmentSchema.config.reflexionGuideline = {};
      }
      this.currentAssessmentSchema.config.reflexionGuideline.enabled = newValue;

      try {
        console.log('Debug - currentAssessmentSchema:', this.currentAssessmentSchema);
        console.log('Debug - schema ID:', this.currentAssessmentSchema?._id);

        if (!this.currentAssessmentSchema?._id) {
          throw new Error('No assessment schema selected');
        }

        // Update the config flag through the dedicated endpoint
        await this.$store.dispatch('assessmentSchemas/updateReflexionGuidelineConfig', {
          id: this.currentAssessmentSchema._id,
          config: {
            enabled: newValue,
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update Reflection Guideline enabled status:', error);

        // Revert the store state since the backend update failed
        this.currentAssessmentSchema.config.reflexionGuideline.enabled = previousEnabled;
        // Store already shows error notification
      }
    },

    // DevTips methods
    /** Build DevTips schedule payload; includes month only for yearly (FA-05.24). */
    buildDevTipsSchedulePayload(overrides = {}) {
      const period = overrides.period ?? this.devTipsPeriodComputed;
      const schedule = {
        period,
        hour: overrides.hour ?? this.devTipsHourComputed,
        dayOfWeek: overrides.dayOfWeek ?? this.devTipsDayOfWeekComputed,
        dayOfMonth: overrides.dayOfMonth ?? this.devTipsDayOfMonthComputed,
      };
      if (period === 'yearly') {
        schedule.month = overrides.month ?? this.devTipsMonthComputed ?? 0;
      }
      return schedule;
    },

    ensureDevTipsScheduleConfig(defaults = {}) {
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.devTips) {
        this.currentAssessmentSchema.config.devTips = {
          enabled: this.devTipsEnabledComputed,
          schedule: {
            period: defaults.period ?? this.devTipsPeriodComputed ?? 'weekly',
            hour: defaults.hour ?? 8,
            dayOfWeek: defaults.dayOfWeek ?? 1,
            dayOfMonth: defaults.dayOfMonth ?? 1,
            month: defaults.month ?? 0,
          },
        };
      }
      return this.currentAssessmentSchema.config.devTips.schedule;
    },

    async onDevTipsEnabledChange(newValue) {
      // Store the previous value in case we need to revert
      const previousEnabled = this.$store.getters['assessmentSchemas/getDevTipsEnabled'](this.currentAssessmentSchema);

      // Optimistically update the store for immediate UI feedback
      this.ensureDevTipsScheduleConfig({ period: 'weekly' });
      this.currentAssessmentSchema.config.devTips.enabled = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              devTips: {
                enabled: newValue,
                schedule: this.buildDevTipsSchedulePayload(),
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update DevTips enabled status:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.devTips) {
          this.currentAssessmentSchema.config.devTips.enabled = previousEnabled;
        }
        // Store already shows error notification
      }
    },

    async onDevTipsPeriodChange(newValue) {
      // Store the previous value in case we need to revert
      const previousPeriod = this.$store.getters['assessmentSchemas/getDevTipsPeriod'](this.currentAssessmentSchema);
      const previousMonth = this.devTipsMonthComputed;

      // Optimistically update the store for immediate UI feedback
      const schedule = this.ensureDevTipsScheduleConfig();
      schedule.period = newValue;
      if (newValue === 'yearly' && schedule.month === undefined) {
        schedule.month = 0;
      }

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              devTips: {
                enabled: this.devTipsEnabledComputed,
                schedule: this.buildDevTipsSchedulePayload({ period: newValue }),
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update DevTips period:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.devTips?.schedule) {
          this.currentAssessmentSchema.config.devTips.schedule.period = previousPeriod;
          this.currentAssessmentSchema.config.devTips.schedule.month = previousMonth;
        }
        // Store already shows error notification
      }
    },

    async onDevTipsHourChange(newValue) {
      const previousHour = this.$store.getters['assessmentSchemas/getDevTipsHour'](this.currentAssessmentSchema);

      const schedule = this.ensureDevTipsScheduleConfig({
        period: this.devTipsPeriodComputed,
      });
      schedule.hour = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              devTips: {
                enabled: this.devTipsEnabledComputed,
                schedule: this.buildDevTipsSchedulePayload({ hour: newValue }),
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update DevTips hour:', error);
        if (this.currentAssessmentSchema.config?.devTips?.schedule) {
          this.currentAssessmentSchema.config.devTips.schedule.hour = previousHour;
        }
      }
    },

    async onDevTipsDayOfWeekChange(newValue) {
      const previousDayOfWeek = this.$store.getters['assessmentSchemas/getDevTipsDayOfWeek'](this.currentAssessmentSchema);

      const schedule = this.ensureDevTipsScheduleConfig({
        period: this.devTipsPeriodComputed,
        hour: this.devTipsHourComputed,
      });
      schedule.dayOfWeek = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              devTips: {
                enabled: this.devTipsEnabledComputed,
                schedule: this.buildDevTipsSchedulePayload({ dayOfWeek: newValue }),
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update DevTips dayOfWeek:', error);
        if (this.currentAssessmentSchema.config?.devTips?.schedule) {
          this.currentAssessmentSchema.config.devTips.schedule.dayOfWeek = previousDayOfWeek;
        }
      }
    },

    async onDevTipsDayOfMonthChange(newValue) {
      const previousDayOfMonth = this.$store.getters['assessmentSchemas/getDevTipsDayOfMonth'](this.currentAssessmentSchema);

      const schedule = this.ensureDevTipsScheduleConfig({
        period: this.devTipsPeriodComputed,
        hour: this.devTipsHourComputed,
        dayOfWeek: this.devTipsDayOfWeekComputed,
      });
      schedule.dayOfMonth = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              devTips: {
                enabled: this.devTipsEnabledComputed,
                schedule: this.buildDevTipsSchedulePayload({ dayOfMonth: newValue }),
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update DevTips dayOfMonth:', error);
        if (this.currentAssessmentSchema.config?.devTips?.schedule) {
          this.currentAssessmentSchema.config.devTips.schedule.dayOfMonth = previousDayOfMonth;
        }
      }
    },

    async onDevTipsMonthChange(newValue) {
      const previousMonth = this.$store.getters['assessmentSchemas/getDevTipsMonth'](this.currentAssessmentSchema);

      const schedule = this.ensureDevTipsScheduleConfig({
        period: this.devTipsPeriodComputed,
        hour: this.devTipsHourComputed,
        dayOfWeek: this.devTipsDayOfWeekComputed,
        dayOfMonth: this.devTipsDayOfMonthComputed,
      });
      schedule.month = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              devTips: {
                enabled: this.devTipsEnabledComputed,
                schedule: this.buildDevTipsSchedulePayload({ month: newValue }),
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update DevTips month:', error);
        if (this.currentAssessmentSchema.config?.devTips?.schedule) {
          this.currentAssessmentSchema.config.devTips.schedule.month = previousMonth;
        }
      }
    },

    /** Save wall-clock schedule as-is (no admin-local → UTC conversion) */
    async onDevTipsScheduleFromLocal(localHour, localDayOfWeek, localDayOfMonth, localMonth) {
      const period = this.devTipsPeriodComputed;
      const prev = {
        hour: this.devTipsHourComputed,
        dayOfWeek: this.devTipsDayOfWeekComputed,
        dayOfMonth: this.devTipsDayOfMonthComputed,
        month: this.devTipsMonthComputed,
      };
      const hour = localHour ?? 8;
      const dayOfWeek = localDayOfWeek ?? 1;
      const dayOfMonth = localDayOfMonth ?? 1;
      const month = localMonth ?? prev.month ?? 0;

      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.devTips) {
        this.currentAssessmentSchema.config.devTips = {
          enabled: this.devTipsEnabledComputed,
          schedule: {
            period,
            hour: 8,
            dayOfWeek: 1,
            dayOfMonth: 1,
            month: 0,
          },
        };
      }
      this.currentAssessmentSchema.config.devTips.schedule.hour = hour;
      this.currentAssessmentSchema.config.devTips.schedule.dayOfWeek = dayOfWeek;
      this.currentAssessmentSchema.config.devTips.schedule.dayOfMonth = dayOfMonth;
      if (period === 'yearly') {
        this.currentAssessmentSchema.config.devTips.schedule.month = month;
      }

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              devTips: {
                enabled: this.devTipsEnabledComputed,
                schedule: this.buildDevTipsSchedulePayload({
                  period,
                  hour,
                  dayOfWeek,
                  dayOfMonth,
                  ...(period === 'yearly' ? { month } : {}),
                }),
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update DevTips schedule:', error);
        if (this.currentAssessmentSchema.config?.devTips?.schedule) {
          this.currentAssessmentSchema.config.devTips.schedule.hour = prev.hour;
          this.currentAssessmentSchema.config.devTips.schedule.dayOfWeek = prev.dayOfWeek;
          this.currentAssessmentSchema.config.devTips.schedule.dayOfMonth = prev.dayOfMonth;
          this.currentAssessmentSchema.config.devTips.schedule.month = prev.month;
        }
      }
    },

    // Level System methods
    async onLevelSystemEnabledChange(newValue) {
      // Store the previous value in case we need to revert
      const previousEnabled = this.$store.getters['assessmentSchemas/getLevelSystemEnabled'](this.currentAssessmentSchema);

      // Optimistically update the store for immediate UI feedback
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.levelSystem) {
        this.currentAssessmentSchema.config.levelSystem = {
          enabled: false,
          xpPerCompletion: 0,
        };
      }
      this.currentAssessmentSchema.config.levelSystem.enabled = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              levelSystem: {
                enabled: newValue,
                xpPerCompletion: this.xpPerCompletionComputed,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update Level System enabled status:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.levelSystem) {
          this.currentAssessmentSchema.config.levelSystem.enabled = previousEnabled;
        }
        // Store already shows error notification
      }
    },

    // Admin Creation methods
    async onCanBeCreatedByAdminChange(newValue) {
      // Store the previous value in case we need to revert
      const previousEnabled = this.$store.getters['assessmentSchemas/getCanBeCreatedByAdmin'](this.currentAssessmentSchema);

      // Optimistically update the store (nested path so getter reflects it)
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.adminCreation) {
        this.currentAssessmentSchema.config.adminCreation = {
          canBeCreatedByAdmin: false,
        };
      }
      this.currentAssessmentSchema.config.adminCreation.canBeCreatedByAdmin = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              adminCreation: {
                canBeCreatedByAdmin: newValue,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update canBeCreatedByAdmin status:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.adminCreation) {
          this.currentAssessmentSchema.config.adminCreation.canBeCreatedByAdmin = previousEnabled;
        }
        // Store already shows error notification
      }
    },

    async onXpPerCompletionChange(newValue) {
      // Handle event object - extract the actual value
      const actualValue = newValue && typeof newValue === 'object' && newValue.value !== undefined
        ? newValue.value
        : newValue;

      // Store the previous value in case we need to revert
      const previousXp = this.$store.getters['assessmentSchemas/getXpPerCompletion'](this.currentAssessmentSchema);

      // Optimistically update the store for immediate UI feedback
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.levelSystem) {
        this.currentAssessmentSchema.config.levelSystem = {
          enabled: this.levelSystemEnabledComputed,
          xpPerCompletion: 0,
        };
      }
      this.currentAssessmentSchema.config.levelSystem.xpPerCompletion = actualValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              levelSystem: {
                enabled: this.levelSystemEnabledComputed,
                xpPerCompletion: actualValue,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update XP per completion:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.levelSystem) {
          this.currentAssessmentSchema.config.levelSystem.xpPerCompletion = previousXp;
        }
        // Store already shows error notification
      }
    },

    // First Login Task methods
    async onIsFirstLoginTaskChange(newValue) {
      // Store the previous value in case we need to revert
      const previousEnabled = this.$store.getters['assessmentSchemas/getFirstLoginTaskEnabled'](this.currentAssessmentSchema);

      // Optimistically update the store for immediate UI feedback
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.firstLoginTask) {
        this.currentAssessmentSchema.config.firstLoginTask = {
          enabled: false,
        };
      }
      this.currentAssessmentSchema.config.firstLoginTask.enabled = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              firstLoginTask: {
                enabled: newValue,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update First Login Task status:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.firstLoginTask) {
          this.currentAssessmentSchema.config.firstLoginTask.enabled = previousEnabled;
        }
        // Store already shows error notification
      }
    },

    syncLocalIntroFromSchema() {
      const intro = this.currentAssessmentSchema?.config?.intro;
      const defaultIntro = {
        enabled: false,
        title: { en: '', de: '' },
        content: { en: '', de: '' },
        buttonText: { en: '', de: '' },
        instructionsHeading: { en: '', de: '' },
        instructionsContent: { en: '', de: '' },
        additionalInfoHeading: { en: '', de: '' },
        additionalInfoContent: { en: '', de: '' },
      };
      if (!intro) {
        this.localIntroConfig = JSON.parse(JSON.stringify(defaultIntro));
        return;
      }

      this.localIntroConfig = {
        enabled: !!intro.enabled,
        title: { en: intro.title?.en ?? '', de: intro.title?.de ?? '' },
        content: { en: intro.content?.en ?? '', de: intro.content?.de ?? '' },
        buttonText: { en: intro.buttonText?.en ?? '', de: intro.buttonText?.de ?? '' },
        instructionsHeading: { en: intro.instructionsHeading?.en ?? '', de: intro.instructionsHeading?.de ?? '' },
        instructionsContent: { en: intro.instructionsContent?.en ?? '', de: intro.instructionsContent?.de ?? '' },
        additionalInfoHeading: { en: intro.additionalInfoHeading?.en ?? '', de: intro.additionalInfoHeading?.de ?? '' },
        additionalInfoContent: { en: intro.additionalInfoContent?.en ?? '', de: intro.additionalInfoContent?.de ?? '' },
      };
    },

    // Handler for intro page checkbox change (show intro page before assessment)
    cleanIntroRichTextHiddenChars() {
      const lang = this.introLang;
      this.localIntroConfig.content[lang] = sanitizeHiddenChars(
        this.localIntroConfig.content[lang] || '',
      );
      this.localIntroConfig.instructionsContent[lang] = sanitizeHiddenChars(
        this.localIntroConfig.instructionsContent[lang] || '',
      );
      this.localIntroConfig.additionalInfoContent[lang] = sanitizeHiddenChars(
        this.localIntroConfig.additionalInfoContent[lang] || '',
      );
    },

    async onCanDisplayIntroPageChange(newValue) {
      // Store the previous value in case we need to revert
      const previousValue = this.currentAssessmentSchema?.config?.intro?.enabled || false;

      // Optimistically update the store for immediate UI feedback
      this.currentAssessmentSchema.config.intro.enabled = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              intro: {
                enabled: newValue,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update intro enabled:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema?.config?.intro) {
          this.currentAssessmentSchema.config.intro.enabled = previousValue;
        }
        // Store already shows error notification
      }
    },

    async saveIntroSettings() {
      if (!this.currentAssessmentSchema?._id) return;

      if (this.localIntroConfig.enabled) {
        const hasTitle = (this.localIntroConfig.title?.en?.trim() || this.localIntroConfig.title?.de?.trim()) ?? '';
        const hasContent = (this.localIntroConfig.content?.en?.trim() || this.localIntroConfig.content?.de?.trim()) ?? '';
        const hasButtonText = (this.localIntroConfig.buttonText?.en?.trim() || this.localIntroConfig.buttonText?.de?.trim()) ?? '';
        if (!hasTitle || !hasContent || !hasButtonText) {
          this.$toast.add({
            severity: 'error',
            summary: 'Validation',
            detail: 'When intro page is enabled, Page Heading, Introduction content and Start Button Text are required (at least EN or DE).',
            life: 5000,
          });
          return;
        }
      }

      this.savingIntroSettings = true;
      try {
        const payload = {
          config: {
            intro: {
              enabled: this.localIntroConfig.enabled,
              title: { en: this.localIntroConfig.title.en, de: this.localIntroConfig.title.de },
              content:
                { en: this.localIntroConfig.content.en, de: this.localIntroConfig.content.de },
              buttonText: {
                en: this.localIntroConfig.buttonText.en,
                de: this.localIntroConfig.buttonText.de,
              },
              instructionsHeading: {
                en: this.localIntroConfig.instructionsHeading.en,
                de: this.localIntroConfig.instructionsHeading.de,
              },
              instructionsContent: {
                en: this.localIntroConfig.instructionsContent.en,
                de: this.localIntroConfig.instructionsContent.de,
              },
              additionalInfoHeading: {
                en: this.localIntroConfig.additionalInfoHeading.en,
                de: this.localIntroConfig.additionalInfoHeading.de,
              },
              additionalInfoContent: {
                en: this.localIntroConfig.additionalInfoContent.en,
                de: this.localIntroConfig.additionalInfoContent.de,
              },
            },
          },
        };
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload,
        });
        if (!this.currentAssessmentSchema.config) this.currentAssessmentSchema.config = {};
        this.currentAssessmentSchema.config.intro = payload.config.intro;
        // Store already shows FA-02.39 success toast
      } catch (error) {
        console.error('Failed to save intro settings:', error);
        // Store already shows error toast
      } finally {
        this.savingIntroSettings = false;
      }
    },

    // External Evaluation Methods
    async loadAvailableSchemasForExternalEvaluation() {
      try {
        // Fetch all schemas from store
        const allSchemas = this.$store.state.assessmentSchemas.assessmentSchemas || [];

        // Filter out current schema and format for dropdown
        this.availableSchemasForExternalEvaluation = allSchemas
          .filter((schema) => schema._id !== this.currentAssessmentSchema?._id)
          .map((schema) => ({
            label: schema.representationProperties?.assessmentName?.en || schema._id,
            value: schema._id,
          }));
      } catch (error) {
        console.error('Failed to load available schemas for external evaluation:', error);
        this.availableSchemasForExternalEvaluation = [];
      }
    },

    async loadExternalEvaluationSchemaInfo() {
      // Store reference to current schema ID to avoid issues if watcher triggers
      const currentSchemaId = this.currentAssessmentSchema?._id;
      const externalSchemaId = this.$store.getters['assessmentSchemas/getExternalEvaluationSchemaId'](this.currentAssessmentSchema);

      try {
        // Reset the not found flag
        this.externalEvaluationSchemaNotFound = false;

        if (externalSchemaId) {
          // Try to find schema in the available schemas list first (already loaded)
          let schema = this.availableSchemasForExternalEvaluation?.find(
            (s) => s.value === externalSchemaId,
          );

          // Also check the full schemas array in store
          if (!schema) {
            const allSchemas = this.$store.state.assessmentSchemas.assessmentSchemas || [];
            const foundSchema = allSchemas.find((s) => s._id === externalSchemaId);
            if (foundSchema) {
              schema = {
                value: foundSchema._id,
                label: foundSchema.representationProperties?.assessmentName?.en || foundSchema._id,
              };
            }
          }

          // If still not found, try to fetch all schemas to refresh the list
          if (!schema) {
            try {
              // Refresh the schemas list without affecting currentAssessmentSchema
              await this.$store.dispatch('assessmentSchemas/fetchAssessments');

              // Check again after refresh
              const refreshedSchemas = this.$store.state.assessmentSchemas.assessmentSchemas || [];
              const foundSchema = refreshedSchemas.find((s) => s._id === externalSchemaId);

              if (foundSchema) {
                schema = {
                  value: foundSchema._id,
                  label: foundSchema.representationProperties?.assessmentName?.en
                    || foundSchema._id,
                };
              }
            } catch (fetchError) {
              console.error('Failed to refresh schemas list:', fetchError);
            }
          }

          // Verify we're still on the same schema (in case navigation happened)
          if (this.currentAssessmentSchema?._id !== currentSchemaId) {
            return; // Schema changed, abort
          }

          if (schema) {
            this.externalEvaluationSchemaName = schema.label || externalSchemaId;
            this.externalEvaluationSchemaNotFound = false;
          } else {
            // Schema not found - it may have been deleted
            this.externalEvaluationSchemaName = '';
            this.externalEvaluationSchemaNotFound = true;
            this.$toast.add({
              severity: 'error',
              summary: 'External Evaluation Schema Not Found',
              detail: `The linked external evaluation schema (ID: ${externalSchemaId}) was not found. It may have been deleted.`,
              life: 5000,
            });
          }
        } else {
          this.externalEvaluationSchemaName = '';
          this.externalEvaluationSchemaNotFound = false;
        }
      } catch (error) {
        console.error('Failed to load external evaluation schema info:', error);
        // Only update if still on the same schema
        if (this.currentAssessmentSchema?._id === currentSchemaId) {
          this.externalEvaluationSchemaNotFound = true;
          this.externalEvaluationSchemaName = '';
          this.$toast.add({
            severity: 'error',
            summary: 'Error Loading Schema',
            detail: 'Failed to verify the external evaluation schema.',
            life: 5000,
          });
        }
      }
    },

    onCanCreateExternalEvaluationChange(newValue) {
      if (newValue) {
        // Update immediately when checking (nested path so getter reflects it)
        this.localCanCreateExternalEvaluation = true;
        if (!this.currentAssessmentSchema.config) {
          this.currentAssessmentSchema.config = {};
        }
        if (!this.currentAssessmentSchema.config.externalEvaluation) {
          this.currentAssessmentSchema.config.externalEvaluation = {
            canCreate: true,
            schemaId: this.$store.getters['assessmentSchemas/getExternalEvaluationSchemaId'](this.currentAssessmentSchema) || undefined,
          };
        } else {
          this.currentAssessmentSchema.config.externalEvaluation.canCreate = true;
        }
      } else {
        // If unchecking: keep checkbox checked (don't update local state) and show warning dialog
        // The checkbox will stay checked because localCanCreateExternalEvaluation remains true
        this.disableExternalEvaluationWarningDialogVisible = true;
      }
    },

    // Handler for canDisplayExternalEmails checkbox change
    async onCanDisplayExternalEmailsChange(newValue) {
      // Store the previous value in case we need to revert
      const previousValue = this.currentAssessmentSchema?.canDisplayExternalEmails || false;

      // Optimistically update the store for immediate UI feedback
      this.currentAssessmentSchema.canDisplayExternalEmails = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            canDisplayExternalEmails: newValue,
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update canDisplayExternalEmails:', error);

        // Revert the store state since the backend update failed
        this.currentAssessmentSchema.canDisplayExternalEmails = previousValue;
        // Store already shows error notification
      }
    },

    // Save participant limits (min and max participants)
    async saveParticipantLimits() {
      // Validate that values are not empty
      const minIsEmpty = this.localMinParticipants === null || this.localMinParticipants === undefined || this.localMinParticipants === '';
      const maxIsEmpty = this.localMaxParticipants === null || this.localMaxParticipants === undefined || this.localMaxParticipants === '';

      if (minIsEmpty || maxIsEmpty) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Participant limits cannot be empty',
          life: 3000,
        });
        return;
      }

      // Validate that min is not greater than max
      if (this.localMinParticipants > this.localMaxParticipants) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Minimum participants cannot be greater than maximum participants',
          life: 3000,
        });
        return;
      }

      // Store the previous values in case we need to revert
      const previousMin = this.currentAssessmentSchema?.minParticipants;
      const previousMax = this.currentAssessmentSchema?.maxParticipants;

      // Optimistically update the store for immediate UI feedback
      this.currentAssessmentSchema.minParticipants = this.localMinParticipants;
      this.currentAssessmentSchema.maxParticipants = this.localMaxParticipants;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            minParticipants: this.localMinParticipants,
            maxParticipants: this.localMaxParticipants,
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update participant limits:', error);

        // Revert the store state since the backend update failed
        this.currentAssessmentSchema.minParticipants = previousMin;
        this.currentAssessmentSchema.maxParticipants = previousMax;
        // Store already shows error notification
      }
    },

    async saveExtensionDays() {
      const isEmpty =
        this.localExtensionDays === null
        || this.localExtensionDays === undefined
        || this.localExtensionDays === '';
      if (isEmpty || this.localExtensionDays < 1) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Extension days must be at least 1',
          life: 3000,
        });
        return;
      }

      const previousValue = this.currentAssessmentSchema?.extensionDays;
      this.currentAssessmentSchema.extensionDays = this.localExtensionDays;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            extensionDays: this.localExtensionDays,
          },
        });
      } catch (error) {
        console.error('Failed to update extension days:', error);
        this.currentAssessmentSchema.extensionDays = previousValue;
      }
    },

    async saveRecreationIntervalDays() {
      const immediate = this.localRecreationImmediateAfterCycle === true;
      const validation = validateRecreationIntervalForSave(
        immediate,
        this.localRecreationIntervalDays,
      );
      if (!validation.ok) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: validation.message,
          life: 3000,
        });
        return;
      }

      const previousImmediate =
        this.currentAssessmentSchema?.recreationImmediateAfterCycle === true;
      const previousDays = this.currentAssessmentSchema?.recreationIntervalDays;
      this.currentAssessmentSchema.recreationImmediateAfterCycle = immediate;
      if (!immediate) {
        this.currentAssessmentSchema.recreationIntervalDays = this.localRecreationIntervalDays;
      }

      const payload = buildRecreationIntervalSavePayload(
        immediate,
        this.localRecreationIntervalDays,
      );

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload,
        });
      } catch (error) {
        console.error('Failed to update recreation interval settings:', error);
        this.currentAssessmentSchema.recreationImmediateAfterCycle = previousImmediate;
        this.currentAssessmentSchema.recreationIntervalDays = previousDays;
      }
    },

    // Save minimum assessments for charts configuration
    async saveMinAssessmentsForCharts() {
      // Validate that value is not empty
      const isEmpty = this.localMinAssessmentsForCharts === null || this.localMinAssessmentsForCharts === undefined || this.localMinAssessmentsForCharts === '';

      if (isEmpty) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Minimum assessments for charts cannot be empty',
          life: 3000,
        });
        return;
      }

      // Store the previous value in case we need to revert
      const previousValue = this.currentAssessmentSchema?.minAssessmentsForCharts;

      // Optimistically update the store for immediate UI feedback
      this.currentAssessmentSchema.minAssessmentsForCharts = this.localMinAssessmentsForCharts;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            minAssessmentsForCharts: this.localMinAssessmentsForCharts,
          },
        });
        // Store already shows FA-02.39 success toast
      } catch (error) {
        console.error('Failed to update minimum assessments for charts:', error);

        // Revert the store state since the backend update failed
        this.currentAssessmentSchema.minAssessmentsForCharts = previousValue;
        // Store already shows error toast
      }
    },

    // Save external evaluation settings with warning dialog
    saveExternalEvaluationSettings() {
      const newSchemaId = this.selectedExternalEvaluationSchemaId;
      // currently selected external evaluation schema id
      const currentSchemaId = this.$store.getters['assessmentSchemas/getExternalEvaluationSchemaId'](this.currentAssessmentSchema);

      // Check if there are any changes
      if (newSchemaId === currentSchemaId) {
        // No change, no need to show warning
        this.$toast.add({
          severity: 'info',
          summary: 'No Changes',
          detail: 'External evaluation schema is already set to the selected value.',
          life: 3000,
        });
        return;
      }
      // Store the pending schema ID and show warning dialog
      this.externalEvaluationWarningDialogVisible = true;
    },

    // Confirm and save external evaluation settings
    async confirmSaveExternalEvaluationSettings() {
      try {
        this.externalEvaluationWarningDialogVisible = false;
        const newSchemaId = this.selectedExternalEvaluationSchemaId;
        const previousSchemaId = this.previousExternalEvaluationSchemaId;

        // If a schema was previously selected and it's different from the new one
        // update current schema with externalEvaluationSchemaId
        // and update child schema with dimensions
        if (previousSchemaId !== newSchemaId) {
          await this.$store.dispatch('assessmentSchemas/updateExternalEvaluationConfig', {
            schemaId: this.currentAssessmentSchema._id,
            canCreateExternalEvaluation: true,
            externalEvaluationSchemaId: newSchemaId,
          });

          // Mark child schema as external evaluation schema
          // Note: We don't copy dimensions here
          // as the existing schema already has its own dimensions
          await this.$store.dispatch('assessmentSchemas/updateChildSchema', {
            externalEvaluationSchemaId: newSchemaId,
            isExternalEvaluationSchema: true,
          });

          if (previousSchemaId !== null) {
            await this.$store.dispatch('assessmentSchemas/updateChildSchema', {
              externalEvaluationSchemaId: previousSchemaId,
              isExternalEvaluationSchema: false,
              dimensions: [],
            });
          }
        }

        this.$toast.add({
          severity: 'success',
          summary: 'External Evaluation Schema Updated',
          detail: 'External evaluation schema configuration updated successfully',
          life: 3000,
        });
      } catch (error) {
        console.error('Failed to update external evaluation schema:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to update external evaluation schema',
          life: 3000,
        });
      }
    },

    // Cancel disabling external evaluation
    cancelDisableExternalEvaluation() {
      this.disableExternalEvaluationWarningDialogVisible = false;
      // Reset local state to null so it uses the schema value (which should still be true)
      this.localCanCreateExternalEvaluation = null;
    },

    // Confirm disabling external evaluation
    async confirmDisableExternalEvaluation() {
      try {
        const childSchemaId = this.selectedExternalEvaluationSchemaId;
        this.disableExternalEvaluationWarningDialogVisible = false;
        await this.$store.dispatch('assessmentSchemas/updateExternalEvaluationConfig', {
          schemaId: this.currentAssessmentSchema._id,
          canCreateExternalEvaluation: false,
          externalEvaluationSchemaId: null,
        });

        await this.$store.dispatch('assessmentSchemas/updateChildSchema', {
          externalEvaluationSchemaId: childSchemaId,
          isExternalEvaluationSchema: false,
          dimensions: [],
        });

        this.$toast.add({
          severity: 'success',
          summary: 'External Evaluation Schema Updated',
          detail: 'External evaluation schema configuration updated successfully',
          life: 3000,
        });

        // After successful update, reset local state to use schema value
        this.localCanCreateExternalEvaluation = null;
      } catch (error) {
        console.error('Failed to disable external evaluation:', error);
        // On error, revert local state back to true
        this.localCanCreateExternalEvaluation = true;
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to disable external evaluation',
          life: 3000,
        });
      }
    },

    // Create External Evaluation Schema Dialog Methods
    openCreateExternalSchemaDialog() {
      this.resetExternalSchemaForm();
      this.createExternalSchemaDialogVisible = true;
    },

    closeCreateExternalSchemaDialog() {
      this.createExternalSchemaDialogVisible = false;
      this.resetExternalSchemaForm();
    },

    resetExternalSchemaForm() {
      this.newExternalSchemaForm = {
        nameEn: '',
        nameDe: '',
        tagsInput: '',
        rotatedQuestionsSchema: false,
        rotationPeriod: 'weekly',
        questionSource: 'dimensions',
        totalQuestions: 6,
      };
    },

    async handleCreateExternalSchema() {
      if (!this.newExternalSchemaForm.nameEn) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please provide at least an English name for the schema.',
          life: 3000,
        });
        return;
      }

      this.creatingExternalSchema = true;

      try {
        // Parse tags from comma-separated input
        const tags = this.newExternalSchemaForm.tagsInput
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag);

        const newSchemaType = this.generatedExternalSchemaType;

        // Step 0: Add the new type to assessmentType enum if it doesn't exist
        const assessmentTypeEnum = this.getEnumByName('assessmentType');
        const typeExists = assessmentTypeEnum?.values?.some(
          (v) => v.value === newSchemaType,
        );

        if (!typeExists) {
          await this.$store.dispatch('enums/addEnumValue', {
            enumName: 'assessmentType',
            valueData: {
              value: newSchemaType,
              description: `External evaluation schema for ${this.currentAssessmentSchema?.type || 'assessment'}`,
            },
          });
        }

        // Build schema payload with auto-generated type
        const schemaData = {
          type: newSchemaType,
          representationProperties: {
            assessmentName: {
              en: this.newExternalSchemaForm.nameEn,
              de: this.newExternalSchemaForm.nameDe || this.newExternalSchemaForm.nameEn,
            },
          },
          tags,
          properties: {
            expirationTime: 1209600000,
            reminderTime: 172800000,
            linkAfterAssessment: '/my-results-dashboard',
            multiplierList: [],
          },
          // Use new config structure for question rotation
          config: {
            questionRotation: this.newExternalSchemaForm.rotatedQuestionsSchema ? {
              enabled: true,
              schedule: {
                period: this.newExternalSchemaForm.rotationPeriod,
                hour: this.questionRotationSchedule.hour,
                dayOfWeek: this.questionRotationSchedule.dayOfWeek,
                dayOfMonth: this.questionRotationSchedule.dayOfMonth,
              },
              questionSource: this.newExternalSchemaForm.questionSource,
              totalQuestions: this.newExternalSchemaForm.totalQuestions,
            } : undefined,
          },
        };

        // Step 1: Create the new schema
        const newSchema = await this.$store.dispatch(
          'assessmentSchemas/createAssessment',
          schemaData,
        );

        if (!newSchema || !newSchema._id) {
          throw new Error('Failed to create schema - no ID returned');
        }

        // Step 2: Link the new schema to the parent (update parent config)
        await this.$store.dispatch('assessmentSchemas/updateExternalEvaluationConfig', {
          schemaId: this.currentAssessmentSchema._id,
          canCreateExternalEvaluation: true,
          externalEvaluationSchemaId: newSchema._id,
        });

        // Step 3: Mark new schema as external evaluation schema
        await this.$store.dispatch('assessmentSchemas/updateChildSchema', {
          externalEvaluationSchemaId: newSchema._id,
          isExternalEvaluationSchema: true,
          dimensions: [],
        });

        // Step 4: Copy dimensions from parent schema to the new schema
        const parentDimensions = this.currentAssessmentSchema.dimensions || [];
        if (parentDimensions.length > 0) {
          // Use the dedicated updateSchemaDimensions action
          // which properly handles dimension assignment
          await this.$store.dispatch('assessmentSchemas/updateSchemaDimensions', {
            schemaId: newSchema._id,
            dimensions: parentDimensions,
          });
        }

        // Update local state
        this.selectedExternalEvaluationSchemaId = newSchema._id;
        this.previousExternalEvaluationSchemaId = newSchema._id;

        // Reload available schemas and external schema info
        await this.loadAvailableSchemasForExternalEvaluation();
        await this.loadExternalEvaluationSchemaInfo();

        this.$toast.add({
          severity: 'success',
          summary: 'Schema Created',
          detail: `External evaluation schema "${this.newExternalSchemaForm.nameEn}" created and linked successfully.`,
          life: 3000,
        });

        this.closeCreateExternalSchemaDialog();
      } catch (error) {
        console.error('Failed to create external evaluation schema:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to create external evaluation schema',
          life: 3000,
        });
      } finally {
        this.creatingExternalSchema = false;
      }
    },

    // Save Question Rotation settings
    async saveQuestionRotationSettings() {
      this.savingQuestionRotation = true;

      const actualValue = this.localTotalQuestions;
      const actualStickyCount = this.localStickyQuestionsCount;
      const previousTotal = this.$store.getters['assessmentSchemas/getTotalQuestions'](this.currentAssessmentSchema);
      const previousStickyCount = this.$store.getters['assessmentSchemas/getStickyQuestionsCount'](this.currentAssessmentSchema);

      try {
        if (actualValue !== previousTotal || actualStickyCount !== previousStickyCount) {
          if (!this.currentAssessmentSchema.config) {
            this.currentAssessmentSchema.config = {};
          }
          if (!this.currentAssessmentSchema.config.questionRotation) {
            this.currentAssessmentSchema.config.questionRotation = {
              enabled: this.questionRotationEnabledComputed,
              schedule: {
                period: this.questionRotationPeriodComputed,
                hour: this.questionRotationHourComputed,
                dayOfWeek: this.questionRotationDayOfWeekComputed,
                dayOfMonth: this.questionRotationDayOfMonthComputed,
              },
              questionSource: 'direct',
              totalQuestions: 6,
              stickyQuestionsEnabled: false,
              stickyQuestionsCount: 0,
            };
          }
          this.currentAssessmentSchema.config.questionRotation.totalQuestions = actualValue;
          this.currentAssessmentSchema.config.questionRotation.stickyQuestionsEnabled = this.stickyQuestionsEnabledComputed;
          this.currentAssessmentSchema.config.questionRotation.stickyQuestionsCount = actualStickyCount;

          await this.$store.dispatch('assessmentSchemas/updateAssessment', {
            id: this.currentAssessmentSchema._id,
            payload: {
              config: {
                questionRotation: {
                  enabled: this.questionRotationEnabledComputed,
                  schedule: {
                    period: this.questionRotationPeriodComputed,
                    hour: this.questionRotationHourComputed,
                    dayOfWeek: this.questionRotationDayOfWeekComputed,
                    dayOfMonth: this.questionRotationDayOfMonthComputed,
                  },
                  questionSource: this.questionSourceComputed,
                  totalQuestions: actualValue,
                  stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                  stickyQuestionsCount: actualStickyCount,
                  stickyQuestionSource: this.stickyQuestionSourceComputed,
                },
              },
            },
          });
          // Store already shows FA-02.39 success toast
        }
      } catch (error) {
        console.error('Failed to save question rotation settings:', error);
        if (this.currentAssessmentSchema.config?.questionRotation) {
          this.currentAssessmentSchema.config.questionRotation.totalQuestions = previousTotal;
          this.currentAssessmentSchema.config.questionRotation.stickyQuestionsCount = previousStickyCount;
        }
        this.localTotalQuestions = previousTotal;
        this.localStickyQuestionsCount = previousStickyCount;
        // Store already shows error toast
      } finally {
        this.savingQuestionRotation = false;
      }
    },

    // Save Level System settings
    async saveLevelSystemSettings() {
      this.savingLevelSystem = true;

      const actualValue = this.localXpPerCompletion;
      const previousXp = this.$store.getters['assessmentSchemas/getXpPerCompletion'](this.currentAssessmentSchema);

      try {
        if (actualValue !== previousXp) {
          // Optimistically update the store (nested path so getter reflects it)
          if (!this.currentAssessmentSchema.config) {
            this.currentAssessmentSchema.config = {};
          }
          if (!this.currentAssessmentSchema.config.levelSystem) {
            this.currentAssessmentSchema.config.levelSystem = {
              enabled: this.levelSystemEnabledComputed,
              xpPerCompletion: 0,
            };
          }
          this.currentAssessmentSchema.config.levelSystem.xpPerCompletion = actualValue;

          await this.$store.dispatch('assessmentSchemas/updateAssessment', {
            id: this.currentAssessmentSchema._id,
            payload: {
              config: {
                levelSystem: {
                  enabled: this.levelSystemEnabledComputed,
                  xpPerCompletion: actualValue,
                },
              },
            },
          });
          // Store already shows FA-02.39 success toast
        }
      } catch (error) {
        console.error('Failed to save level system settings:', error);
        // Revert on error
        if (this.currentAssessmentSchema.config?.levelSystem) {
          this.currentAssessmentSchema.config.levelSystem.xpPerCompletion = previousXp;
        }
        this.localXpPerCompletion = previousXp;
        // Store already shows error toast
      } finally {
        this.savingLevelSystem = false;
      }
    },

    async sendDevTipsNow() {
      try {
        this.sendingDevTips = true;
        this.devTipsLoading = true;
        this.devTipsError = null;
        this.devTipsResultsOverlayVisible = true;

        console.log('Sending DevTips now for assessment:', this.currentAssessmentSchema._id);

        // Call the store action for immediate DevTips sending
        const response = await this.$store.dispatch(
          'assessmentSchemas/sendDevTipsForSchema',
          this.currentAssessmentSchema._id,
        );

        // Show results in overlay
        this.devTipsResults = response.data.data;
        this.devTipsLoading = false;

        console.log('DevTips sent successfully:', response.data);

        // Also show a success toast
        this.$toast.add({
          severity: 'success',
          summary: 'DevTips Sent',
          detail: `DevTips sent successfully! ${response.data.data.tipsRecommended} tips recommended to ${response.data.data.usersProcessed} users.`,
          life: 3000,
        });
      } catch (error) {
        console.error('Failed to send DevTips:', error);
        this.devTipsError = error.message || 'Failed to send DevTips';
        this.devTipsLoading = false;

        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: this.devTipsError,
          life: 3000,
        });
      } finally {
        this.sendingDevTips = false;
      }
    },

    closeDevTipsResultsOverlay() {
      this.devTipsResultsOverlayVisible = false;
      this.devTipsResults = null;
      this.devTipsError = null;
    },

    // Reflection Guidelines Trigger Methods
    testReflectionGuidelines() {
      // Prevent multiple confirmation dialogs
      if (this.triggeringReflectionGuidelines) {
        return;
      }

      this.testReflectionGuidelinesDialogVisible = true;
    },

    confirmTestReflectionGuidelines() {
      this.testReflectionGuidelinesDialogVisible = false;
      this.$toast.add({
        severity: 'info',
        summary: 'Test Mode',
        detail: 'Reflection guidelines test completed. Configuration is valid.',
        life: 3000,
      });
    },

    triggerReflectionGuidelinesNow() {
      // Prevent multiple confirmation dialogs
      if (this.triggeringReflectionGuidelines) {
        return;
      }

      this.triggerReflectionGuidelinesDialogVisible = true;
    },

    confirmTriggerReflectionGuidelines() {
      this.triggerReflectionGuidelinesDialogVisible = false;
      this.executeReflectionGuidelinesTrigger();
    },

    async executeReflectionGuidelinesTrigger() {
      try {
        this.triggeringReflectionGuidelines = true;

        console.log('Triggering reflection guidelines now for assessment:', this.currentAssessmentSchema._id);

        // Call the store action for immediate reflection guidelines triggering
        const response = await this.$store.dispatch(
          'assessmentSchemas/triggerReflectionGuidelinesForSchema',
          this.currentAssessmentSchema._id,
        );

        console.log('Reflection guidelines triggered successfully:', response.data);

        // Show success toast
        this.$toast.add({
          severity: 'success',
          summary: 'Reflection Guidelines Triggered',
          detail: response.data.message || 'Reflection guidelines triggered successfully!',
          life: 3000,
        });
      } catch (error) {
        console.error('Failed to trigger reflection guidelines:', error);

        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to trigger reflection guidelines',
          life: 3000,
        });
      } finally {
        this.triggeringReflectionGuidelines = false;
      }
    },

    // Auto Dimensions Matching Methods
    async scanQuestionDimensions() {
      // Prevent scanning if read-only
      if (this.isDimensionsReadOnly) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Read-Only',
          detail: 'Auto dimension matching is disabled for external evaluation schemas',
          life: 3000,
        });
        return;
      }

      try {
        this.scanningDimensions = true;
        this.dimensionMatches = [];
        this.scannedDimensions = false;

        // Ensure dimensions are loaded
        if (this.getAllDimensions.length === 0) {
          await this.$store.dispatch('dimensions/fetchDimensions');
        }

        const questions = this.currentAssessmentSchema.assessmentQuestionArray || [];
        if (questions.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'No Questions',
            detail: 'No questions found to scan for dimensions',
            life: 3000,
          });
          return;
        }

        // Extract unique dimensions from questions
        const questionDimensions = new Set();
        questions.forEach((question) => {
          if (question.properties?.dimensionName && question.properties.dimensionName.trim()) {
            questionDimensions.add(question.properties.dimensionName.trim());
          }
        });

        // Get currently assigned dimensions
        const currentDimensions = this.currentAssessmentSchema.dimensions || [];
        const assignedDimensionValues = currentDimensions.map((d) => d.value);

        // Match with platform dimensions (100% exact match)
        const matches = [];
        questionDimensions.forEach((questionDimension) => {
          const platformDimension = this.getDimensionByValue(questionDimension);

          if (platformDimension) {
            // Check if this dimension is already assigned to the schema
            const isAlreadyAssigned = assignedDimensionValues.includes(questionDimension);

            // Count questions with this dimension
            const questionsCount = questions
              .filter((q) => q.properties?.dimensionName === questionDimension).length;

            matches.push({
              questionDimension,
              platformDimension,
              status: isAlreadyAssigned ? 'already_assigned' : 'matched',
              questionsCount,
            });
          } else {
            // Count questions with this dimension
            const questionsCount = questions
              .filter((q) => q.properties?.dimensionName === questionDimension).length;

            matches.push({
              questionDimension,
              platformDimension: null,
              status: 'no_match',
              questionsCount,
            });
          }
        });

        this.dimensionMatches = matches;
        this.scannedDimensions = true;

        const matchedCount = matches.filter((m) => m.status === 'matched').length;
        const alreadyAssignedCount = matches.filter((m) => m.status === 'already_assigned').length;
        const noMatchCount = matches.filter((m) => m.status === 'no_match').length;

        this.$toast.add({
          severity: 'success',
          summary: 'Scan Complete',
          detail: `Found ${matchedCount} new matches, ${alreadyAssignedCount} already assigned, and ${noMatchCount} unmatched dimensions`,
          life: 3000,
        });
      } catch (error) {
        console.error('Error scanning question dimensions:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Scan Failed',
          detail: 'Failed to scan question dimensions',
          life: 3000,
        });
      } finally {
        this.scanningDimensions = false;
      }
    },

    async applyDimensionMatches() {
      // Prevent applying matches if read-only
      if (this.isDimensionsReadOnly) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Read-Only',
          detail: 'Auto dimension matching is disabled for external evaluation schemas',
          life: 3000,
        });
        return;
      }

      try {
        this.applyingMatches = true;

        const matchedDimensions = this.dimensionMatches.filter((m) => m.status === 'matched');
        if (matchedDimensions.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'No Matches',
            detail: 'No matched dimensions to apply',
            life: 3000,
          });
          return;
        }

        // Update the schema with new dimensions using the proper action
        await this.$store.dispatch('assessmentSchemas/updateSchemaDimensions', {
          schemaId: this.currentAssessmentSchema._id,
          dimensions: matchedDimensions.map((match) => match.platformDimension),
        });

        // Refresh the assessment schema to get updated data
        await this.refreshAssessmentSchema();

        this.$toast.add({
          severity: 'success',
          summary: 'Matches Applied',
          detail: `Successfully added ${matchedDimensions.length} dimensions to the schema`,
          life: 3000,
        });

        // Clear matches after successful application
        this.clearDimensionMatches();
      } catch (error) {
        console.error('Error applying dimension matches:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Apply Failed',
          detail: 'Failed to apply dimension matches',
          life: 3000,
        });
      } finally {
        this.applyingMatches = false;
      }
    },

    clearDimensionMatches() {
      // Prevent clearing if read-only (for consistency, though this only clears local state)
      if (this.isDimensionsReadOnly) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Read-Only',
          detail: 'Auto dimension matching is disabled for external evaluation schemas',
          life: 3000,
        });
        return;
      }
      this.dimensionMatches = [];
      this.scannedDimensions = false;
    },

    // Schema templates are now managed at tenant level, not schema level
    // These methods are no longer needed:
    // async loadSchemaTemplates() { ... }
    // async handleSaveTemplates(templates) { ... }
    // handleCancelTemplates() { ... }
    async loadReflexionGuidelineConfig() {
      try {
        if (!this.currentAssessmentSchema || !this.currentAssessmentSchema._id) {
          console.warn('Cannot load reflection guideline config: no assessment schema selected');
          this.reflexionGuidelineConfig = {};
          return;
        }
        const config = await this.$store.dispatch('assessmentSchemas/getReflexionGuidelineConfig', this.currentAssessmentSchema._id);
        this.reflexionGuidelineConfig = config || {};
      } catch (error) {
        console.error('Error loading reflection guideline config:', error);
        this.reflexionGuidelineConfig = {};
        // Error toast is already handled by the action
      }
    },

    onReflexionGuidelineConfigSaved(config) {
      this.reflexionGuidelineConfig = config;
      // Success toast is already handled by the action
    },

    // Question rotation methods
    async onQuestionRotationEnabledChange(newValue) {
      // Store the previous value in case we need to revert
      const previousEnabled = this.$store.getters['assessmentSchemas/getQuestionRotationEnabled'](this.currentAssessmentSchema);

      // Optimistically update the store for immediate UI feedback
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {
          enabled: false,
          schedule: {
            period: 'weekly',
            hour: 8,
            dayOfWeek: 1,
            dayOfMonth: 1,
          },
          questionSource: 'direct',
          totalQuestions: 6,
        };
      }
      this.currentAssessmentSchema.config.questionRotation.enabled = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: newValue,
                schedule: {
                  period: this.questionRotationPeriodComputed,
                  hour: this.questionRotationHourComputed,
                  dayOfWeek: this.questionRotationDayOfWeekComputed,
                  dayOfMonth: this.questionRotationDayOfMonthComputed,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                stickyQuestionsCount: this.stickyQuestionsCountComputed,
                stickyQuestionSource: this.stickyQuestionSourceComputed,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update question rotation enabled status:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.questionRotation) {
          this.currentAssessmentSchema.config.questionRotation.enabled = previousEnabled;
        }
        // Store already shows error notification
      }
    },

    async onViewRefGuidelineResultsChange(newValue) {
      // Store the previous value in case we need to revert
      const previousEnabled = this.$store.getters['assessmentSchemas/getViewRefGuidelineResults'](this.currentAssessmentSchema);
      const initialConfig = this.reflexionGuidelineInitialConfig;

      // Optimistically update the store for immediate UI feedback
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.reflexionGuideline) {
        this.currentAssessmentSchema.config.reflexionGuideline = {
          viewResults: false,
        };
      }
      this.currentAssessmentSchema.config.reflexionGuideline.viewResults = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              reflexionGuideline: {
                ...initialConfig,
                viewResults: newValue,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update First Login Task status:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.reflexionGuideline && this.currentAssessmentSchema.config.reflexionGuideline.viewResults) {
          this.currentAssessmentSchema.config.reflexionGuideline.viewResults = previousEnabled;
        }
        // Store already shows error notification
      }
    },

    async onQuestionRotationPeriodChange(newValue) {
      // Store the previous value in case we need to revert
      const previousPeriod = this.$store.getters['assessmentSchemas/getQuestionRotationPeriod'](this.currentAssessmentSchema);

      // Optimistically update the store for immediate UI feedback
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {
          enabled: this.questionRotationEnabledComputed,
          schedule: {
            period: 'weekly',
            hour: 8,
            dayOfWeek: 1,
            dayOfMonth: 1,
          },
          questionSource: 'direct',
          totalQuestions: 6,
        };
      }
      this.currentAssessmentSchema.config.questionRotation.schedule.period = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period: newValue,
                  hour: this.questionRotationHourComputed,
                  dayOfWeek: this.questionRotationDayOfWeekComputed,
                  dayOfMonth: this.questionRotationDayOfMonthComputed,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                stickyQuestionsCount: this.stickyQuestionsCountComputed,
                stickyQuestionSource: this.stickyQuestionSourceComputed,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update question rotation period:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.questionRotation?.schedule) {
          this.currentAssessmentSchema.config.questionRotation.schedule.period = previousPeriod;
        }
        // Store already shows error notification
      }
    },

    async onQuestionRotationHourChange(newValue) {
      const previousHour = this.$store.getters['assessmentSchemas/getQuestionRotationHour'](this.currentAssessmentSchema);

      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {
          enabled: this.questionRotationEnabledComputed,
          schedule: {
            period: this.questionRotationPeriodComputed,
            hour: 8,
            dayOfWeek: 1,
            dayOfMonth: 1,
          },
          questionSource: 'direct',
          totalQuestions: 6,
        };
      }
      this.currentAssessmentSchema.config.questionRotation.schedule.hour = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period: this.questionRotationPeriodComputed,
                  hour: newValue,
                  dayOfWeek: this.questionRotationDayOfWeekComputed,
                  dayOfMonth: this.questionRotationDayOfMonthComputed,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                stickyQuestionsCount: this.stickyQuestionsCountComputed,
                stickyQuestionSource: this.stickyQuestionSourceComputed,
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update question rotation hour:', error);
        if (this.currentAssessmentSchema.config?.questionRotation?.schedule) {
          this.currentAssessmentSchema.config.questionRotation.schedule.hour = previousHour;
        }
      }
    },

    async onQuestionRotationDayOfWeekChange(newValue) {
      const previousDayOfWeek = this.$store.getters['assessmentSchemas/getQuestionRotationDayOfWeek'](this.currentAssessmentSchema);

      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {
          enabled: this.questionRotationEnabledComputed,
          schedule: {
            period: this.questionRotationPeriodComputed,
            hour: this.questionRotationHourComputed,
            dayOfWeek: 1,
            dayOfMonth: 1,
          },
          questionSource: 'direct',
          totalQuestions: 6,
        };
      }
      this.currentAssessmentSchema.config.questionRotation.schedule.dayOfWeek = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period: this.questionRotationPeriodComputed,
                  hour: this.questionRotationHourComputed,
                  dayOfWeek: newValue,
                  dayOfMonth: this.questionRotationDayOfMonthComputed,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                stickyQuestionsCount: this.stickyQuestionsCountComputed,
                stickyQuestionSource: this.stickyQuestionSourceComputed,
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update question rotation dayOfWeek:', error);
        if (this.currentAssessmentSchema.config?.questionRotation?.schedule) {
          this.currentAssessmentSchema.config
            .questionRotation.schedule.dayOfWeek = previousDayOfWeek;
        }
      }
    },

    async onQuestionRotationDayOfMonthChange(newValue) {
      const previousDayOfMonth = this.$store.getters['assessmentSchemas/getQuestionRotationDayOfMonth'](this.currentAssessmentSchema);

      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {
          enabled: this.questionRotationEnabledComputed,
          schedule: {
            period: this.questionRotationPeriodComputed,
            hour: this.questionRotationHourComputed,
            dayOfWeek: this.questionRotationDayOfWeekComputed,
            dayOfMonth: 1,
          },
          questionSource: 'direct',
          totalQuestions: 6,
        };
      }
      this.currentAssessmentSchema.config.questionRotation.schedule.dayOfMonth = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period: this.questionRotationPeriodComputed,
                  hour: this.questionRotationHourComputed,
                  dayOfWeek: this.questionRotationDayOfWeekComputed,
                  dayOfMonth: newValue,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                stickyQuestionsCount: this.stickyQuestionsCountComputed,
                stickyQuestionSource: this.stickyQuestionSourceComputed,
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update question rotation dayOfMonth:', error);
        if (this.currentAssessmentSchema.config?.questionRotation?.schedule) {
          this.currentAssessmentSchema.config
            .questionRotation.schedule.dayOfMonth = previousDayOfMonth;
        }
      }
    },

    /** Save wall-clock schedule as-is (no admin-local → UTC conversion) */
    async onQuestionRotationScheduleFromLocal(localHour, localDayOfWeek, localDayOfMonth) {
      const period = this.questionRotationPeriodComputed;
      const prev = {
        hour: this.questionRotationHourComputed,
        dayOfWeek: this.questionRotationDayOfWeekComputed,
        dayOfMonth: this.questionRotationDayOfMonthComputed,
      };
      const hour = localHour ?? 8;
      const dayOfWeek = localDayOfWeek ?? 1;
      const dayOfMonth = localDayOfMonth ?? 1;

      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {
          enabled: this.questionRotationEnabledComputed,
          schedule: {
            period,
            hour: 8,
            dayOfWeek: 1,
            dayOfMonth: 1,
          },
          questionSource: 'direct',
          totalQuestions: 6,
        };
      }
      this.currentAssessmentSchema.config.questionRotation.schedule.hour = hour;
      this.currentAssessmentSchema.config.questionRotation.schedule.dayOfWeek = dayOfWeek;
      this.currentAssessmentSchema.config.questionRotation.schedule.dayOfMonth = dayOfMonth;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period,
                  hour,
                  dayOfWeek,
                  dayOfMonth,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                stickyQuestionsCount: this.stickyQuestionsCountComputed,
                stickyQuestionSource: this.stickyQuestionSourceComputed,
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update question rotation schedule:', error);
        if (this.currentAssessmentSchema.config?.questionRotation?.schedule) {
          const s = this.currentAssessmentSchema.config.questionRotation.schedule;
          s.hour = prev.hour;
          s.dayOfWeek = prev.dayOfWeek;
          s.dayOfMonth = prev.dayOfMonth;
        }
      }
    },

    async onQuestionSourceChange(newValue) {
      // Store the previous value in case we need to revert
      const previousSource = this.$store.getters['assessmentSchemas/getQuestionSource'](this.currentAssessmentSchema);

      // Optimistically update the store for immediate UI feedback
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {
          enabled: this.questionRotationEnabledComputed,
          schedule: {
            period: this.questionRotationPeriodComputed,
            hour: this.questionRotationHourComputed,
            dayOfWeek: this.questionRotationDayOfWeekComputed,
            dayOfMonth: this.questionRotationDayOfMonthComputed,
          },
          questionSource: 'direct',
          totalQuestions: 6,
        };
      }
      this.currentAssessmentSchema.config.questionRotation.questionSource = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period: this.questionRotationPeriodComputed,
                  hour: this.questionRotationHourComputed,
                  dayOfWeek: this.questionRotationDayOfWeekComputed,
                  dayOfMonth: this.questionRotationDayOfMonthComputed,
                },
                questionSource: newValue,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                stickyQuestionsCount: this.stickyQuestionsCountComputed,
                stickyQuestionSource: this.stickyQuestionSourceComputed,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update question source:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.questionRotation) {
          this.currentAssessmentSchema.config.questionRotation.questionSource = previousSource;
        }
        // Store already shows error notification
      }
    },

    async onTotalQuestionsChange(newValue) {
      // Handle event object - extract the actual value
      const actualValue = newValue && typeof newValue === 'object' && newValue.value !== undefined
        ? newValue.value
        : newValue;

      // Store the previous value in case we need to revert
      const previousTotal = this.$store.getters['assessmentSchemas/getTotalQuestions'](this.currentAssessmentSchema);

      // Optimistically update the store for immediate UI feedback
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {
          enabled: this.questionRotationEnabledComputed,
          schedule: {
            period: this.questionRotationPeriodComputed,
            hour: this.questionRotationHourComputed,
            dayOfWeek: this.questionRotationDayOfWeekComputed,
            dayOfMonth: this.questionRotationDayOfMonthComputed,
          },
          questionSource: 'direct',
          totalQuestions: 6,
        };
      }
      this.currentAssessmentSchema.config.questionRotation.totalQuestions = actualValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period: this.questionRotationPeriodComputed,
                  hour: this.questionRotationHourComputed,
                  dayOfWeek: this.questionRotationDayOfWeekComputed,
                  dayOfMonth: this.questionRotationDayOfMonthComputed,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: actualValue,
              },
            },
          },
        });
        // Store already shows success notification
      } catch (error) {
        console.error('Failed to update total questions:', error);

        // Revert the store state since the backend update failed
        if (this.currentAssessmentSchema.config?.questionRotation) {
          this.currentAssessmentSchema.config.questionRotation.totalQuestions = previousTotal;
        }
        // Store already shows error notification
      }
    },

    async onStickyQuestionsEnabledChange(newValue) {
      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {};
      }
      this.currentAssessmentSchema.config.questionRotation.stickyQuestionsEnabled = newValue;

      if (!newValue) {
        this.localStickyQuestionsCount = 0;
        this.currentAssessmentSchema.config.questionRotation.stickyQuestionsCount = 0;
      }

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period: this.questionRotationPeriodComputed,
                  hour: this.questionRotationHourComputed,
                  dayOfWeek: this.questionRotationDayOfWeekComputed,
                  dayOfMonth: this.questionRotationDayOfMonthComputed,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: newValue,
                stickyQuestionsCount: newValue ? this.localStickyQuestionsCount : 0,
                stickyQuestionSource: this.stickyQuestionSourceComputed,
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update sticky questions enabled:', error);
        this.currentAssessmentSchema.config.questionRotation.stickyQuestionsEnabled = !newValue;
      }
    },

    async onStickyQuestionsCountChange(newValue) {
      const actualValue = newValue && typeof newValue === 'object' && newValue.value !== undefined
        ? newValue.value
        : newValue;

      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {};
      }
      this.currentAssessmentSchema.config.questionRotation.stickyQuestionsCount = actualValue;
    },

    async onStickyQuestionSourceChange(newValue) {
      const previousSource = this.$store.getters['assessmentSchemas/getStickyQuestionSource'](this.currentAssessmentSchema);

      if (!this.currentAssessmentSchema.config) {
        this.currentAssessmentSchema.config = {};
      }
      if (!this.currentAssessmentSchema.config.questionRotation) {
        this.currentAssessmentSchema.config.questionRotation = {};
      }
      this.currentAssessmentSchema.config.questionRotation.stickyQuestionSource = newValue;

      try {
        await this.$store.dispatch('assessmentSchemas/updateAssessment', {
          id: this.currentAssessmentSchema._id,
          payload: {
            config: {
              questionRotation: {
                enabled: this.questionRotationEnabledComputed,
                schedule: {
                  period: this.questionRotationPeriodComputed,
                  hour: this.questionRotationHourComputed,
                  dayOfWeek: this.questionRotationDayOfWeekComputed,
                  dayOfMonth: this.questionRotationDayOfMonthComputed,
                },
                questionSource: this.questionSourceComputed,
                totalQuestions: this.totalQuestionsComputed,
                stickyQuestionsEnabled: this.stickyQuestionsEnabledComputed,
                stickyQuestionsCount: this.stickyQuestionsCountComputed,
                stickyQuestionSource: newValue,
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update sticky question source:', error);
        this.currentAssessmentSchema.config.questionRotation.stickyQuestionSource = previousSource;
      }
    },

    /**
     * Create assessment from schema
     */
    async createAssessmentFromSchema() {
      try {
        this.creatingAssessment = true;

        // Create assessments for ALL active users (not just current user)
        // Send empty payload to trigger creation for all users
        const result = await this.$store.dispatch('assessmentSchemas/createAssessmentFromSchema', {
          schemaId: this.currentAssessmentSchema._id,
          // No userId = create for all users
          // No options = use schema defaults
        });

        if (result) {
          const totalAssessments = result.totalAssessments || 1;
          this.$toast.add({
            severity: 'success',
            summary: 'Assessments Created',
            detail: `Successfully created ${totalAssessments} assessment(s) from schema for all active users!`,
            life: 5000,
          });
        }
      } catch (error) {
        console.error('Failed to create assessment from schema:', error);
        const data = error.response?.data;
        if (data?.error === 'ASSESSMENT_ASSIGNMENT_ROLE_DENIED') {
          // Dialog is opened globally from assessmentSchemas store action
        } else {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail:
              (typeof data?.message === 'string' && data.message)
              || error.message
              || 'Failed to create assessment from schema',
            life: 5000,
          });
        }
      } finally {
        this.creatingAssessment = false;
      }
    },

    /**
     * Show the generate test data warning dialog
     */
    showGenerateTestDataDialog() {
      this.generateTestDataDialogVisible = true;
    },

    /**
     * Close the generate test data dialog
     */
    closeGenerateTestDataDialog() {
      this.generateTestDataDialogVisible = false;
    },

    /**
     * Confirm and execute test data generation
     */
    async confirmGenerateTestData() {
      try {
        this.generatingTestData = true;
        this.closeGenerateTestDataDialog();

        const result = await this.$store.dispatch('assessmentSchemas/generateTestData', {
          schemaId: this.currentAssessmentSchema._id,
        });

        if (result) {
          this.$toast.add({
            severity: 'success',
            summary: 'Test Data Generated',
            detail: result.message || 'Test data generated successfully!',
            life: 8000,
          });
        }
      } catch (error) {
        console.error('Failed to generate test data:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to generate test data',
          life: 5000,
        });
      } finally {
        this.generatingTestData = false;
      }
    },

    /**
     * Validate schema structure for development purposes
     */
    validateSchemaStructure() {
      try {
        const schema = this.currentAssessmentSchema;
        const validationResults = {
          hasName: !!(schema.representationProperties?.assessmentName?.en),
          hasDimensions: !!(schema.dimensions && schema.dimensions.length > 0),
          hasQuestions: !!(schema.assessmentQuestionArray
           && schema.assessmentQuestionArray.length > 0),
          hasValidQuestions: this.hasInvalidQuestions === false,
          hasUnassignedDimensions: this.hasUnassignedDimensions === false,
          totalDimensions: schema.dimensions?.length || 0,
          totalQuestions: schema.assessmentQuestionArray?.length || 0,
          questionTypes: schema.assessmentQuestionArray?.map((q) => q.type) || [],
          dimensionValues: Array.isArray(schema.dimensions)
            ? schema.dimensions.map((d) => d.value) : [],
        };

        console.log('🔍 Schema Validation Results:', validationResults);

        this.$toast.add({
          severity: 'info',
          summary: 'Schema Validation',
          detail: 'Validation complete. Check console for details.',
          life: 5000,
        });
      } catch (error) {
        console.error('Schema validation failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Failed to validate schema structure',
          life: 3000,
        });
      }
    },

    /**
     * Export schema data for development purposes
     */
    exportSchemaData() {
      try {
        const schema = this.currentAssessmentSchema;
        const exportData = {
          schema,
          validation: {
            hasInvalidQuestions: this.hasInvalidQuestions,
            hasUnassignedDimensions: this.hasUnassignedDimensions,
            invalidQuestions: this.getInvalidQuestions,
            unassignedDimensions: this.getUnassignedDimensions,
          },
          exportDate: new Date().toISOString(),
          exportPurpose: 'development_debugging',
        };

        // Create and download JSON file
        const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `schema-${schema._id}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$toast.add({
          severity: 'success',
          summary: 'Export Successful',
          detail: 'Schema data exported successfully',
          life: 3000,
        });
      } catch (error) {
        console.error('Schema export failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Export Error',
          detail: 'Failed to export schema data',
          life: 3000,
        });
      }
    },
  },
};
</script>

<style scoped>
.assessment-details-container {
  height: 100%;
  overflow: hidden;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
}

/* Tabs styling for smart height calculation */
.assessment-details-container :deep(.p-tabs) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.assessment-details-container :deep(.p-tabview) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.assessment-details-container :deep(.p-tabview-panels) {
  flex: 1;
  overflow: hidden;
  padding: 0;
  /* Smart height: 100vh - header height - selected
   assessment header - tab header height - margins */
  max-height: calc(100vh - 280px);
}

.assessment-details-container :deep(.p-tabview-panel) {
  height: 100%;
  overflow-y: auto;
  /* Smooth scrolling */
  scroll-behavior: smooth;
  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
  /* Ensure content can scroll within the panel */
  max-height: calc(100vh - 200px);
}

/* Custom scrollbar for webkit browsers */
.assessment-details-container :deep(.p-tabview-panel)::-webkit-scrollbar {
  width: 6px;
}

.assessment-details-container :deep(.p-tabview-panel)::-webkit-scrollbar-track {
  background: #f7fafc;
  border-radius: 3px;
}

.assessment-details-container :deep(.p-tabview-panel)::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.assessment-details-container :deep(.p-tabview-panel)::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Tab content wrapper for scrollable content */
.tab-content-wrapper {
  height: 100%;
  overflow-y: auto;
  /* Smart height calculation: 100vh - header - selected assessment header - tab header - margins */
  max-height: calc(100vh - 280px);
  /* Smooth scrolling */
  scroll-behavior: smooth;
  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

/* Custom scrollbar for tab content wrapper */
.tab-content-wrapper::-webkit-scrollbar {
  width: 6px;
}

.tab-content-wrapper::-webkit-scrollbar-track {
  background: #f7fafc;
  border-radius: 3px;
}

.tab-content-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.tab-content-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

</style>
